import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';
import * as yup from 'yup';

import moment from 'moment';
import TextInput from '../../../../../Form/TextInput';
import SingleFileUploader from '../Documents/SingleFileUploader';
import MultiFileUploader from '../Documents/MultiFileUploader';
import TextInputSwitch from '../../../../../Form/TextInputSwitch';
import PhoneInput from '../../../../../Form/PhoneInput';
import FormDropdown from '../../../../../Form/FormDropdown';
import DatePicker from '../../../../../Form/DatePicker';
import Textarea from '../../../../../Form/Textarea';
import RadioButton from '../../../../../Form/RadioButton';
import Switch from '../../../../../GradientSwitch';
import SectionNavigator from '../SectionNavigator';
import Button from '../../../../../AutoButton';
import ChipsDropdown from '../../../../../Form/ChipsDropdown';
import MultiDropdown from '../../../../../Form/MultiDropdown';
import { COUNTRY_OPTIONS } from '../../../../../../constants/main';
import DatePickerSwitch from '../../../../../Form/DatePickerSwitch';
import classes from './styles.module.scss';
import AdminService from '../../../../../../services/AdminService';
import { useUiContext } from '../../../../../../context/UiContext';
import FXRatesService from '../../../../../../services/FXRatesService';

const PRIMARY_TABS = [
  'Personal',
  'Work Auth',
  'Languages',
  'Experience',
  'Education',
  'Skills',
  'Comp & Availability',
  'Documents',
];

// Allow Unicode letters, spaces, apostrophes and hyphens
const NAME_REGEX = /^[\p{L}' -]+$/u;

// Generate unique ID for form items
const generateId = () =>
  `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// Helper function to safely parse dates - returns empty string if invalid
const safeParseDate = (dateValue, formatString = null) => {
  if (!dateValue) return '';

  let date;
  if (formatString) {
    date = new Date(`${dateValue}-01`);
  } else {
    date = new Date(dateValue);
  }

  // Check if date is valid
  // eslint-disable-next-line no-restricted-globals
  if (isNaN(date.getTime())) {
    return '';
  }

  return date;
};

// Deep comparison helper to check if form values have changed
const deepEqual = (obj1, obj2) => {
  if (obj1 === obj2) return true;

  if (obj1 == null || obj2 == null) return obj1 === obj2;

  if (typeof obj1 !== 'object' || typeof obj2 !== 'object') {
    return obj1 === obj2;
  }

  // Handle arrays
  if (Array.isArray(obj1) && Array.isArray(obj2)) {
    if (obj1.length !== obj2.length) return false;
    return obj1.every((item, index) => deepEqual(item, obj2[index]));
  }

  // Handle arrays vs objects
  if (Array.isArray(obj1) !== Array.isArray(obj2)) return false;

  // Handle objects
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  return keys1.every((key) => deepEqual(obj1[key], obj2[key]));
};

export default function CandidateForm({
  resumeData,
  candidate,
  onAdded,
  refetchCandidates,
  onFormValuesChange,
  onProgressChange,
}) {
  const { t } = useTranslation();
  const {
    showModal,
    setIsCreatingOrUpdating,
    setIsFetching,
    showUnknownErrorModal,
  } = useUiContext();
  const [activeSection, setActiveSection] = useState('Personal');
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const formContentRef = useRef(null);
  const formikRef = useRef(null);
  const didPrefillSkillsRef = useRef(false);
  const didPrefillEducationRef = useRef(false);
  const didPrefillLanguagesRef = useRef(false);
  // const didPrefillGenderRef = useRef(false);
  const prevProgressRef = useRef(null);

  // LLM-suggested skills not found in the skill DB — user can promote each one-click.
  const [potentialSkills, setPotentialSkills] = useState([]);
  const [addingPotentialSkill, setAddingPotentialSkill] = useState(null);
  const didInitPotentialSkillsRef = useRef(false);

  const isEditMode = !!candidate?.id;

  // FX Equivalent helpers and snapshot (one snapshot per mount)
  const [fxSnapshot, setFxSnapshot] = useState(null);
  useEffect(() => {
    let mounted = true;
    FXRatesService.getLatestRatesUSD()
      .then((r) => {
        if (mounted) setFxSnapshot(r);
      })
      .catch(() => {
        if (mounted) setFxSnapshot(null);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const getPeriodFactor = (freqVal) => {
    const map = {
      Hour: 2080,
      Month: 12,
      Annual: 1,
      Year: 1,
      Yearly: 1,
      Monthly: 12,
      Hourly: 2080,
    };
    return map[freqVal] ?? 1;
  };

  const getCurrencySymbol = (code) => {
    if (code === 'INR') return '₹';
    if (code === 'EUR') return '€';
    return '$';
  };

  const formatFx = (amount, code) => {
    if (amount == null) return '-';
    const fd = code === 'INR' ? 0 : 2;
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: code,
        minimumFractionDigits: fd,
        maximumFractionDigits: fd,
      }).format(amount);
    } catch (e) {
      return `${getCurrencySymbol(code)}${Number(amount).toFixed(fd)}`;
    }
  };

  const computeFxEquivalent = (source) => {
    const amount = Number(source?.amount ?? 0);
    const currency = source?.currency?.value;
    const freq = source?.frequency?.value;
    const targetCurrency = source?.fx_target_currency?.value;
    const targetFreq = source?.fx_target_frequency?.value;
    if (!amount || amount < 0) return null;
    if (!currency || !freq || !targetCurrency || !targetFreq) return null;
    if (!fxSnapshot?.rates) return null;

    const factor = getPeriodFactor(freq);
    const annualBase = amount * factor;
    const srcRate = fxSnapshot.rates[currency];
    if (!srcRate) return null;
    const annualUsd = annualBase / srcRate;

    const targetAnnual = annualUsd * (fxSnapshot.rates[targetCurrency] || 0);
    const eq = targetAnnual / getPeriodFactor(targetFreq);
    if (!Number.isFinite(eq)) return null;
    return eq;
  };

  // Normalize text for matching (remove accents/diacritics, lowercase, trim)
  const normalizeForMatch = (str) =>
    String(str || '')
      .normalize('NFD')
      // remove diacritics
      .replace(/[\u0300-\u036f]/g, '')
      // remove common apostrophes/quotes variants
      .replace(
        /["'\u2019\u2018\u2032\u02BC\uFF07\u201A\u201B\u275B\u275C]+/g,
        ''
      )
      // unify hyphen-like characters to simple hyphen
      .replace(/[\u2010\u2011\u2012\u2013\u2014\u2015]/g, '-')
      .toLowerCase()
      // collapse extra whitespace
      .replace(/\s+/g, ' ')
      .trim();

  function getRequiredFields() {
    return [
      'personal.first_name',
      'personal.last_name',
      'personal.email',
      'personal.phone',
      'personal.country',
      'personal.city',
      'personal.source',
      'skills.key',
      'availability.notice_period_days',
      'documents.linkedin',
      'documents.resume',
    ];
  }

  function getInitialValues(parsed) {
    const c = candidate;
    const initial = {
      personal: {
        first_name: c?.firstName || parsed?.personalInfo?.firstName || '',
        middle_name: c?.middleName || parsed?.personalInfo?.middleName || '',
        last_name: c?.lastName || parsed?.personalInfo?.lastName || '',
        email: c?.email || parsed?.personalInfo?.email || '',
        phone: c?.phone || parsed?.personalInfo?.phone || '',
        city: c?.city || parsed?.personalInfo?.city || '',
        country: (() => {
          if (c?.country) {
            return COUNTRY_OPTIONS.find((option) => option.value === c.country);
          }
          const parsedCountryCode = parsed?.personalInfo?.countryCode;
          if (parsedCountryCode) {
            return COUNTRY_OPTIONS.find(
              (option) => option.value === parsedCountryCode
            );
          }
          return null;
        })(),
        dob: c?.birthDate || parsed?.personalInfo?.birthDate || '',
        gender: c?.genderId
          ? { label: c?.gender?.name || '', value: c.genderId }
          : null,
        source: c?.source ? { label: c?.source || '', value: c?.source } : null,
        externalSource: c?.externalSource
          ? { label: c?.externalSource || '', value: c?.externalSource }
          : null,
        nationality: (() => {
          if (c?.nationality) {
            // API returns country codes (e.g., "DE,FR")
            return c.nationality
              .split(',')
              .map((n) => n.trim())
              .filter(Boolean)
              .map((code) => COUNTRY_OPTIONS.find((o) => o.value === code))
              .filter(Boolean);
          }
          // Prefill from parsed resume nationality
          const parsedNationality = parsed?.personalInfo?.nationality;
          if (parsedNationality) {
            const nationalities = Array.isArray(parsedNationality)
              ? parsedNationality
              : [parsedNationality];
            return nationalities
              .map((n) => String(n || '').trim())
              .filter(Boolean)
              .map((val) => {
                // Try to find by code first
                let option = COUNTRY_OPTIONS.find((o) => o.value === val);
                // If not found, try to find by label (case-insensitive)
                if (!option) {
                  option = COUNTRY_OPTIONS.find(
                    (o) => o.label.toLowerCase() === val.toLowerCase()
                  );
                }
                return option;
              })
              .filter(Boolean);
          }
          return [];
        })(),
      },
      workauth: (c?.workAuthorizations || []).map((wa) => ({
        id: wa.id,
        country: COUNTRY_OPTIONS.find((o) => o.value === wa.country) || null,
        status: wa.visaStatus
          ? { label: wa.visaStatus.name, value: wa.visaStatus.id }
          : null,
        visa_type: wa.visaType || '',
        expiry: wa.visaStatusDate || '',
      })),
      languages: (c?.careerLanguageSelections || []).map((lang) => ({
        id: lang.id || generateId(),
        language: lang.language
          ? { label: lang.language.name || '', value: lang.language.id }
          : null,
        languageLevel: lang.languageLevel
          ? {
              label: lang.languageLevel.name || '',
              value: lang.languageLevel.id,
            }
          : null,
      })),
      experience: {
        fresh_grad: !!c?.isFresher,
        entries: c?.careerExperiences
          ? c.careerExperiences.map((e) => ({
              id: e.id || generateId(),
              employer: e.empoyerName || e.company || '',
              title: e.jobTitle || '',
              start: safeParseDate(e.startDate),
              end: safeParseDate(e.endDate),
              current: !!e.isCurrent,
              summary: e.summary || e.description || '',
            }))
          : (parsed?.experience || []).map((e) => ({
              id: generateId(),
              employer: e.company || '',
              title: e.jobTitle || '',
              start: safeParseDate(e.startDate, 'YYYY-MM'),
              end: safeParseDate(e.endDate, 'YYYY-MM'),
              current: !!e.isCurrent,
              summary: e.description || '',
            })),
      },
      education: (c?.careerEducations || []).map((ed) => ({
        id: ed.id || generateId(),
        degree: ed.graduationId
          ? {
              label: ed.graduation?.name || String(ed.graduationId),
              value: ed.graduationId,
            }
          : null,
        institution: ed.institution || '',
        year: ed.isOngoing ? null : ed.graduationYear || null,
        ongoing: !!ed.isOngoing,
      })),
      skills: {
        key: (c?.skills || []).map((s) => ({ label: s.name, value: s.id })),
        certifications:
          c?.certifications ||
          (Array.isArray(resumeData?.certifications)
            ? resumeData.certifications.join(', ')
            : resumeData?.certifications || ''),
        upskill: c?.willingToUpSkill ?? null,
      },
      compensation: {
        engagement_type: c?.engagementTypeId
          ? { label: c?.engagementType?.name || '', value: c.engagementTypeId }
          : null,
        current: {
          amount: c?.currentSalary || '',
          currency: c?.currentSalaryCurrencyCode
            ? {
                label: c.currentSalaryCurrencyCode,
                value: c.currentSalaryCurrencyCode,
              }
            : { label: 'INR', value: 'INR' },
          frequency: c?.currentSalaryFrequency
            ? {
                value: c.currentSalaryFrequency,
                label: t(
                  `dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.SalaryFrequencyOptions.${c.currentSalaryFrequency}`
                ),
              }
            : {
                value: 'Annual',
                label: t(
                  'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.SalaryFrequencyOptions.Annual'
                ),
              },
          fx_target_currency: { label: 'USD', value: 'USD' },
          fx_target_frequency: {
            value: 'Annual',
            label: t(
              'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.SalaryFrequencyOptions.Annual'
            ),
          },
          note: c?.currentSalaryInfo || '',
        },
        expected: (() => {
          // Parsed résumé may ship a structured `expectedSalary` object
          // (see Backend ResumeExpectedSalary). Map it into the form shape
          // when the saved candidate entity has nothing yet.
          const parsedExp = parsed?.expectedSalary || null;
          const parsedFreqKey = (() => {
            const raw = parsedExp?.frequency;
            if (!raw) return null;
            const key = String(raw).trim().toLowerCase();
            if (key === 'hourly' || key === 'hour' || key === 'per hour')
              return 'Hour';
            if (key === 'monthly' || key === 'month' || key === 'per month')
              return 'Month';
            if (
              key === 'annual' ||
              key === 'annually' ||
              key === 'yearly' ||
              key === 'year' ||
              key === 'per annum' ||
              key === 'p.a.'
            )
              return 'Annual';
            // Daily, Week, BiWeek or unknown → let user pick, don't mis-map.
            return null;
          })();
          const freqLabel = (v) =>
            t(
              `dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.SalaryFrequencyOptions.${v}`
            );

          const amountFromParsed =
            parsedExp?.amount != null && parsedExp.amount !== ''
              ? String(parsedExp.amount)
              : '';

          const currencyOption = (() => {
            if (c?.expectedSalaryCurrencyCode) {
              return {
                label: c.expectedSalaryCurrencyCode,
                value: c.expectedSalaryCurrencyCode,
              };
            }
            if (parsedExp?.currencyCode) {
              return {
                label: parsedExp.currencyCode,
                value: parsedExp.currencyCode,
              };
            }
            return { label: 'INR', value: 'INR' };
          })();

          const frequencyOption = (() => {
            if (c?.exptecedSalaryFrequency) {
              return {
                value: c.exptecedSalaryFrequency,
                label: freqLabel(c.exptecedSalaryFrequency),
              };
            }
            if (parsedFreqKey) {
              return {
                value: parsedFreqKey,
                label: freqLabel(parsedFreqKey),
              };
            }
            return { value: 'Annual', label: freqLabel('Annual') };
          })();

          return {
            amount: c?.expectedSalary || amountFromParsed || '',
            currency: currencyOption,
            frequency: frequencyOption,
            fx_target_currency: { label: 'USD', value: 'USD' },
            fx_target_frequency: {
              value: 'Annual',
              label: freqLabel('Annual'),
            },
            note: c?.expectedSalaryInfo || parsedExp?.notes || '',
          };
        })(),
      },
      availability: {
        notice_period_days: c?.noticePeriodId
          ? {
              label: String(c?.noticePeriod?.name || c.noticePeriodId),
              value: c.noticePeriodId,
            }
          : null,
        immediate: !!c?.immediateJoiner,
        from_date: c?.availableFrom || '',
        shift: c?.workShiftId
          ? { label: c?.workShift?.name || '', value: c.workShiftId }
          : null,
        shift_readiness: c?.workShiftReadinessId
          ? {
              label: c?.workShiftReadiness?.name || '',
              value: c.workShiftReadinessId,
            }
          : null,
        tz_overlap: c?.timeZoneOverlap
          ? { label: c.timeZoneOverlap, value: c.timeZoneOverlap }
          : null,
        work_mode: c?.jobLocationTypeId
          ? {
              label: c?.jobLocationType?.name || '',
              value: c.jobLocationTypeId,
            }
          : null,
        preference: c?.engagementPreferenceId
          ? {
              label: c?.engagementPreference?.name || '',
              value: c.engagementPreferenceId,
            }
          : null,
        travel: {
          willing: c?.willingToTravel ?? null,
          percent: c?.travelPercentage
            ? {
                label: `${c.travelPercentage}%`,
                value: String(c.travelPercentage),
              }
            : { label: '0%', value: '0' },
        },
        relocate: {
          willing: c?.willingToRelocate ?? null,
          cities: c?.travelCities || null,
        },
      },
      documents: {
        linkedin: c?.linkedIn || resumeData?.personalInfo?.linkedIn || '',
        github: c?.github || resumeData?.personalInfo?.github || '',
        portfolio: c?.portfolio || resumeData?.personalInfo?.portfolio || '',
        // Prefill resume: prefer existing candidate resume in edit mode, otherwise parser upload
        resume: c?.careerResume || resumeData?.resumeFile || null,
        additional: (c?.careerOtherDocuments || []).map((d) => ({
          name: d.displayName || d.fileName,
          fileName: d.fileName,
          displayName: d.displayName,
          url: d.url,
          mimeType: d.mimeType,
        })),
      },
    };

    if (!candidate) {
      return initial;
    }

    return initial;
  }

  // Prefill helpers: capture initial values with and without resumeData
  const initialValuesWithResume = useMemo(
    () => getInitialValues(resumeData),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [resumeData, candidate, t]
  );
  const initialValuesWithoutResume = useMemo(
    () => getInitialValues(null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [candidate, t]
  );

  // Determine baseline values based on scenario
  const baselineValues = useMemo(() => {
    // Scenario 1: Resume parsed - always show popup, but still track baseline
    if (resumeData && !candidate) {
      return initialValuesWithResume;
    }
    // Scenario 2: Empty form - compare with defaults (no resume, no candidate)
    if (!resumeData && !candidate) {
      return initialValuesWithoutResume;
    }
    // Scenario 3: Edit mode - compare with prefilled candidate values
    if (candidate) {
      return initialValuesWithResume;
    }
    return initialValuesWithoutResume;
  }, [
    resumeData,
    candidate,
    initialValuesWithResume,
    initialValuesWithoutResume,
  ]);

  const currencyOptions = useMemo(
    () => [
      { label: 'USD', value: 'USD' },
      { label: 'EUR', value: 'EUR' },
      { label: 'INR', value: 'INR' },
    ],
    []
  );

  const sourceOptions = useMemo(
    () => [
      { label: 'Direct Application', value: 'Direct Application' },
      { label: 'Direct Email', value: 'Direct Email' },
      { label: 'Internal Referral', value: 'Internal Referral' },
      { label: 'External Referral', value: 'External Referral' },
      { label: 'External Source', value: 'External Source' },
      { label: 'Agency Submission', value: 'Agency Submission' },
      { label: 'Career Page', value: 'Career Page' },
      { label: 'Campus / College', value: 'Campus / College' },
      { label: 'Talent Pool', value: 'Talent Pool' },
      { label: 'Other', value: 'Other' },
    ],
    []
  );

  const externalSourceOptions = useMemo(
    () => [
      { label: 'LinkedIn', value: 'LinkedIn' },
      { label: 'Facebook', value: 'Facebook' },
      { label: 'Zip recruiter', value: 'Zip recruiter' },
      { label: 'Indeed', value: 'Indeed' },
      { label: 'Naukri', value: 'Naukri' },
      { label: 'Dice', value: 'Dice' },
      { label: 'Monster', value: 'Monster' },
      { label: 'Others', value: 'Others' },
    ],
    []
  );

  const frequencyOptions = useMemo(
    () => [
      {
        value: 'Hour',
        label: t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.SalaryFrequencyOptions.Hour'
        ),
      },
      {
        value: 'Week',
        label: t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.SalaryFrequencyOptions.Week'
        ),
      },
      {
        value: 'BiWeek',
        label: t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.SalaryFrequencyOptions.BiWeek'
        ),
      },
      {
        value: 'Month',
        label: t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.SalaryFrequencyOptions.Month'
        ),
      },
      {
        value: 'Annual',
        label: t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.SalaryFrequencyOptions.Annual'
        ),
      },
    ],
    [t]
  );

  const timezoneOptions = useMemo(
    () => [
      { label: 'EST', value: 'EST' },
      { label: 'PST', value: 'PST' },
      { label: 'CET', value: 'CET' },
      { label: 'APAC', value: 'APAC' },
    ],
    []
  );

  const travelPercentOptions = useMemo(() => {
    const options = [];
    for (let i = 0; i <= 100; i += 5) {
      options.push({ label: `${i}%`, value: `${i}` });
    }
    return options;
  }, []);

  useEffect(() => {
    // Prefill skills from parsed resume values by fetching all skills and matching by lowercase contains
    if (didPrefillSkillsRef.current) return;

    const parsedSkills = Array.isArray(resumeData?.skills)
      ? resumeData.skills
      : [];
    if (!parsedSkills.length) return;

    const fetchAllSkillsAndPrefill = async () => {
      try {
        let pageNumber = 1;
        const pageSize = 5000;
        const allSkills = [];
        // Fetch all pages (defensive against pagination)
        // eslint-disable-next-line no-constant-condition
        while (true) {
          // eslint-disable-next-line no-await-in-loop
          const { data, nextPage } = await AdminService.getSkills({
            orderBy: 'sortOrder',
            sortOrder: 'asc',
            term: '',
            pageNumber,
            pageSize,
          });
          if (Array.isArray(data)) {
            allSkills.push(...data);
          }
          if (!nextPage) break;
          pageNumber = nextPage;
        }

        const parsedLower = parsedSkills
          .map((s) => (s == null ? '' : String(s).trim().toLowerCase()))
          .filter(Boolean);
        if (!parsedLower.length) return;

        // Match each parsed skill to backend skills by exact name match (case-insensitive)
        const matchedOptions = parsedLower
          .map((parsedSkill) => {
            const exactMatch = allSkills.find(
              (skill) => String(skill?.name || '').toLowerCase() === parsedSkill
            );
            if (exactMatch) {
              return { label: exactMatch.name, value: exactMatch.id };
            }
            return null;
          })
          .filter(Boolean);
        if (!matchedOptions.length) return;

        if (formikRef.current) {
          const current = formikRef.current.values?.skills?.key;
          const looksLikeParsedPlaceholders = Array.isArray(current)
            ? current.some(
                (v) => typeof v?.value === 'string' && v?.value.includes('-')
              )
            : false;

          if (
            !Array.isArray(current) ||
            current.length === 0 ||
            looksLikeParsedPlaceholders
          ) {
            formikRef.current.setFieldValue('skills.key', matchedOptions, true);
            didPrefillSkillsRef.current = true;
          }
        }
      } catch (e) {
        // Silent fail: do not block form if prefill fails
      }
    };

    fetchAllSkillsAndPrefill();

    // no cleanup needed
  }, [resumeData]);

  // Initialize potential-skill suggestions from the parsed resume (skills the LLM
  // identified but could not map to the existing skill DB).
  useEffect(() => {
    if (didInitPotentialSkillsRef.current) return;
    const raw = Array.isArray(resumeData?.potentialSkills)
      ? resumeData.potentialSkills
      : [];
    const seen = new Set();
    const cleaned = raw
      .map((s) => (typeof s === 'string' ? s.trim() : ''))
      .filter((s) => {
        if (!s) return false;
        const key = s.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    if (cleaned.length) {
      setPotentialSkills(cleaned);
    }
    didInitPotentialSkillsRef.current = true;
  }, [resumeData]);

  const dismissPotentialSkill = (name) => {
    setPotentialSkills((prev) =>
      prev.filter((s) => s.toLowerCase() !== name.toLowerCase())
    );
  };

  const adoptPotentialSkill = async (name) => {
    if (!name || addingPotentialSkill) return;
    setAddingPotentialSkill(name);
    try {
      const created = await AdminService.createSkill({
        name,
        note: null,
        sortOrder: null,
      });
      const option = {
        label: created?.name || name,
        value: created?.id,
      };
      if (option.value != null && formikRef.current) {
        const current = Array.isArray(formikRef.current.values?.skills?.key)
          ? formikRef.current.values.skills.key
          : [];
        const alreadySelected = current.some((v) => v?.value === option.value);
        const next = alreadySelected ? current : [...current, option];
        formikRef.current.setFieldValue('skills.key', next, true);
        formikRef.current.setFieldTouched('skills.key', true, true);
      }
      dismissPotentialSkill(name);
    } catch (e) {
      showUnknownErrorModal?.();
    } finally {
      setAddingPotentialSkill(null);
    }
  };

  useEffect(() => {
    // Prefill education degree for each parsed education entry by fetching all available educations and matching by lowercase contains
    if (didPrefillEducationRef.current) return;

    const parsedEdus = Array.isArray(resumeData?.education)
      ? resumeData.education
      : [];
    if (!parsedEdus.length) return;

    const fetchAllEducationsAndPrefill = async () => {
      try {
        let pageNumber = 1;
        const pageSize = 100;
        const allEducations = [];
        // eslint-disable-next-line no-constant-condition
        while (true) {
          // eslint-disable-next-line no-await-in-loop
          const { data, nextPage } = await AdminService.getEducations({
            orderBy: 'sortOrder',
            sortOrder: 'asc',
            term: '',
            pageNumber,
            pageSize,
          });
          if (Array.isArray(data)) {
            allEducations.push(...data);
          }
          if (!nextPage) break;
          pageNumber = nextPage;
        }

        if (!formikRef.current) return;
        const currentEducation = formikRef.current.values?.education || [];

        const makeOption = (e) => ({ label: e.name, value: e.id });
        const options = allEducations.map(makeOption);

        // If the form education is empty, construct it from the parser and only match the degree from DB
        if (currentEducation.length === 0) {
          const constructedEducation = (parsedEdus || []).map((p) => {
            const parsedDegreeRaw = p?.degreeType || '';
            const parsedDegree = normalizeForMatch(parsedDegreeRaw);

            let matchedDegree = null;
            if (parsedDegree) {
              for (let j = 0; j < options.length; j += 1) {
                const opt = options[j];
                const optNameNormalized = normalizeForMatch(opt?.label);
                if (
                  optNameNormalized &&
                  optNameNormalized.includes(parsedDegree)
                ) {
                  matchedDegree = opt;
                  break;
                }
              }
            }

            return {
              id: generateId(),
              degree: matchedDegree,
              institution: p?.institution || '',
              year: p?.graduationYear || '',
              ongoing: !p?.graduationYear,
            };
          });

          if (constructedEducation.length) {
            formikRef.current.setFieldValue(
              'education',
              constructedEducation,
              true
            );
            didPrefillEducationRef.current = true;
          }
          return;
        }

        const updatedEducation = currentEducation.map((ed, idx) => {
          // Already set by user? keep as-is
          if (ed?.degree && typeof ed.degree?.value !== 'string') {
            return ed;
          }

          const parsedDegreeRaw =
            parsedEdus[idx]?.degree ||
            parsedEdus[idx]?.degreeType ||
            parsedEdus[idx]?.level ||
            parsedEdus[idx]?.degreeName ||
            '';
          const parsedDegree = normalizeForMatch(parsedDegreeRaw);
          if (!parsedDegree) return ed;

          // Try to find first option whose name contains parsed degree
          for (let j = 0; j < options.length; j += 1) {
            const opt = options[j];
            const optNameNormalized = normalizeForMatch(opt?.label);
            if (optNameNormalized && optNameNormalized.includes(parsedDegree)) {
              return { ...ed, degree: opt };
            }
          }
          return ed;
        });

        // Apply only if any change
        const changed = updatedEducation.some(
          (ed, i) => ed?.degree !== currentEducation[i]?.degree
        );
        if (changed) {
          formikRef.current.setFieldValue('education', updatedEducation, true);
          didPrefillEducationRef.current = true;
        }
      } catch (e) {
        // Silent fail: do not block form if prefill fails
      }
    };

    fetchAllEducationsAndPrefill();
  }, [resumeData]);

  useEffect(() => {
    // Prefill languages from parsed resume values by fetching all languages and language levels
    // and matching by normalized string comparison
    if (didPrefillLanguagesRef.current) return;
    if (isEditMode) return; // Skip for edit mode - already has candidate languages

    // Languages are under personalInfo.languages with code and level properties
    const parsedLanguages = Array.isArray(resumeData?.personalInfo?.languages)
      ? resumeData.personalInfo.languages
      : [];
    if (!parsedLanguages.length) return;

    const fetchAllLanguagesAndPrefill = async () => {
      try {
        // Fetch all languages
        let pageNumber = 1;
        const pageSize = 500;
        const allLanguages = [];
        // eslint-disable-next-line no-constant-condition
        while (true) {
          // eslint-disable-next-line no-await-in-loop
          const { data, nextPage } = await AdminService.getLanguages({
            orderBy: 'sortOrder',
            sortOrder: 'asc',
            term: '',
            pageNumber,
            pageSize,
          });
          if (Array.isArray(data)) {
            allLanguages.push(...data);
          }
          if (!nextPage) break;
          pageNumber = nextPage;
        }

        // Fetch all language levels
        pageNumber = 1;
        const allLanguageLevels = [];
        // eslint-disable-next-line no-constant-condition
        while (true) {
          // eslint-disable-next-line no-await-in-loop
          const { data, nextPage } = await AdminService.getLanguageLevels({
            orderBy: 'sortOrder',
            sortOrder: 'asc',
            term: '',
            pageNumber,
            pageSize,
          });
          if (Array.isArray(data)) {
            allLanguageLevels.push(...data);
          }
          if (!nextPage) break;
          pageNumber = nextPage;
        }

        if (!formikRef.current) return;

        // Build matched languages list using array methods
        // Properties are: code (language name) and level (proficiency level)
        const matchedLanguages = parsedLanguages
          .map((parsed) => {
            const parsedLangNorm = normalizeForMatch(parsed?.code);
            const parsedLevelNorm = normalizeForMatch(parsed?.level);

            if (!parsedLangNorm) return null;

            // Find matching language by normalized name (case-insensitive, partial match)
            const matchedLang = allLanguages.find((l) => {
              const nameLower = normalizeForMatch(l.name);
              return (
                nameLower === parsedLangNorm ||
                nameLower.includes(parsedLangNorm) ||
                parsedLangNorm.includes(nameLower)
              );
            });

            if (!matchedLang) return null;

            // Find matching language level by normalized name (case-insensitive, partial match)
            let matchedLevel = null;
            if (parsedLevelNorm) {
              matchedLevel = allLanguageLevels.find((lvl) => {
                const levelLower = normalizeForMatch(lvl.name);
                return (
                  levelLower === parsedLevelNorm ||
                  levelLower.includes(parsedLevelNorm) ||
                  parsedLevelNorm.includes(levelLower)
                );
              });
            }

            return {
              id: generateId(),
              language: { label: matchedLang.name, value: matchedLang.id },
              languageLevel: matchedLevel
                ? { label: matchedLevel.name, value: matchedLevel.id }
                : null,
            };
          })
          .filter(Boolean);

        if (!matchedLanguages.length) return;

        // Only set if current languages are empty or not yet prefilled
        const current = formikRef.current.values?.languages;
        if (!Array.isArray(current) || current.length === 0) {
          formikRef.current.setFieldValue('languages', matchedLanguages, true);
          didPrefillLanguagesRef.current = true;
        }
      } catch (e) {
        // Silent fail: do not block form if prefill fails
      }
    };

    fetchAllLanguagesAndPrefill();
  }, [resumeData, isEditMode]);

  // useEffect(() => {
  //   // Prefill gender from parsed resume by fetching all genders and matching
  //   if (didPrefillGenderRef.current) return;
  //   if (isEditMode) return; // Skip for edit mode - already has candidate gender

  //   const parsedGender = resumeData?.personalInfo?.gender;
  //   if (!parsedGender) return;

  //   const fetchGendersAndPrefill = async () => {
  //     try {
  //       const { data } = await AdminService.getGenders({
  //         orderBy: 'sortOrder',
  //         sortOrder: 'asc',
  //         term: '',
  //         pageNumber: 1,
  //         pageSize: 100,
  //       });

  //       if (!Array.isArray(data) || !data.length) return;
  //       if (!formikRef.current) return;

  //       const parsedGenderNorm = normalizeForMatch(parsedGender);

  //       // Find matching gender by normalized name (case-insensitive)
  //       const matchedGender = data.find((g) => {
  //         const nameLower = normalizeForMatch(g.name);
  //         return (
  //           nameLower === parsedGenderNorm ||
  //           nameLower.includes(parsedGenderNorm) ||
  //           parsedGenderNorm.includes(nameLower)
  //         );
  //       });

  //       if (!matchedGender) return;

  //       // Only set if current gender is not set
  //       const current = formikRef.current.values?.personal?.gender;
  //       if (!current) {
  //         formikRef.current.setFieldValue(
  //           'personal.gender',
  //           { label: matchedGender.name, value: matchedGender.id },
  //           true
  //         );
  //         didPrefillGenderRef.current = true;
  //       }
  //     } catch (e) {
  //       // Silent fail: do not block form if prefill fails
  //     }
  //   };

  //   fetchGendersAndPrefill();
  // }, [resumeData, isEditMode]);

  const validationSchema = useMemo(() => {
    return yup.object({
      personal: yup.object({
        first_name: yup
          .string()
          .trim()
          .min(2, t('common.minLength', { count: 2 }))
          .max(50, t('common.maxLength', { count: 50 }))
          .matches(NAME_REGEX, t('common.invalidCharacters'))
          .required(t('common.cannotBeEmpty')),
        middle_name: yup
          .string()
          .trim()
          .max(50, t('common.maxLength', { count: 50 }))
          .nullable(),
        last_name: yup
          .string()
          .trim()
          .min(2, t('common.minLength', { count: 2 }))
          .max(50, t('common.maxLength', { count: 50 }))
          .matches(NAME_REGEX, t('common.invalidCharacters'))
          .required(t('common.cannotBeEmpty')),
        email: yup
          .string()
          .trim()
          .email(t('common.emailIsNotValid'))
          .required(t('common.cannotBeEmpty')),
        phone: yup
          .string()
          .trim()
          .required(t('common.cannotBeEmpty'))
          .test(
            'is-valid-phone',
            t(
              'dashboardComponents.Console.CompanyManagement.addEdit.errorMessages.phoneInvalid'
            ),
            async (value) => {
              if (!value) {
                return true; // Skip validation if the value is empty
              }

              const { isValidPhoneNumber, AsYouType } = await import(
                'libphonenumber-js'
              );

              const formattedNumber = new AsYouType().input(value);

              const formattedNumberWithPlus = formattedNumber.startsWith('+')
                ? formattedNumber
                : `+${formattedNumber}`;

              return isValidPhoneNumber(
                new AsYouType().input(formattedNumberWithPlus)
              );
            }
          ),
        source: yup.object().nullable().required(t('common.cannotBeEmpty')),
        externalSource: yup.object().nullable(),
        city: yup.string().trim().required(t('common.cannotBeEmpty')),
        country: yup.object().nullable().required(t('common.cannotBeEmpty')),
        dob: yup
          .mixed()
          .test('is-18', t('intelCandidate.form.mustBeAtLeast18'), (value) => {
            if (!value) return true;
            const d = new Date(value);
            if (Number.isNaN(d.getTime())) return true;
            const now = new Date();
            const eighteen = new Date(
              now.getFullYear() - 18,
              now.getMonth(),
              now.getDate()
            );
            return d <= eighteen;
          }),
      }),
      workauth: yup.array().of(
        yup.object({
          country: yup.object().nullable().required(t('common.cannotBeEmpty')),
          status: yup.object().nullable().required(t('common.cannotBeEmpty')),
          visa_type: yup.string().nullable(),
          expiry: yup
            .date()
            .nullable()
            .typeError(t('intelCandidate.form.workAuth.invalidExpiryDate'))
            .required(t('intelCandidate.form.workAuth.expiryDateRequired')),
        })
      ),
      experience: yup.object({
        fresh_grad: yup.boolean(),
        entries: yup.array().of(
          yup.object({
            employer: yup.string().test(
              'skip-if-fresh-grad',
              t('common.cannotBeEmpty'),
              // eslint-disable-next-line func-names
              function validateEmployer(value) {
                // eslint-disable-next-line react/no-this-in-sfc
                const rootValue =
                  // eslint-disable-next-line react/no-this-in-sfc
                  this.from && this.from.length > 0
                    ? // eslint-disable-next-line react/no-this-in-sfc
                      this.from[this.from.length - 1]?.value
                    : null;
                const isFreshGrad = rootValue?.experience?.fresh_grad === true;
                // Skip validation if fresh_grad is true
                if (isFreshGrad) {
                  return true;
                }
                // Otherwise require the field
                return !!value && value.trim().length > 0;
              }
            ),
            title: yup.string().test(
              'skip-if-fresh-grad',
              t('common.cannotBeEmpty'),
              // eslint-disable-next-line func-names
              function validateTitle(value) {
                // eslint-disable-next-line react/no-this-in-sfc
                const rootValue =
                  // eslint-disable-next-line react/no-this-in-sfc
                  this.from && this.from.length > 0
                    ? // eslint-disable-next-line react/no-this-in-sfc
                      this.from[this.from.length - 1]?.value
                    : null;
                const isFreshGrad = rootValue?.experience?.fresh_grad === true;
                // Skip validation if fresh_grad is true
                if (isFreshGrad) {
                  return true;
                }
                // Otherwise require the field
                return !!value && value.trim().length > 0;
              }
            ),
            start: yup
              .date()
              .nullable()
              .test(
                'skip-if-fresh-grad',
                t('intelCandidate.form.experience.startDateRequired'),
                // eslint-disable-next-line func-names
                function validateStart(value) {
                  // eslint-disable-next-line react/no-this-in-sfc
                  const rootValue =
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.from && this.from.length > 0
                      ? // eslint-disable-next-line react/no-this-in-sfc
                        this.from[this.from.length - 1]?.value
                      : null;
                  const isFreshGrad =
                    rootValue?.experience?.fresh_grad === true;
                  // Skip validation if fresh_grad is true
                  if (isFreshGrad) {
                    return true;
                  }
                  // Otherwise validate the date
                  if (!value) return false;
                  if (moment(value).isAfter(moment())) {
                    // eslint-disable-next-line react/no-this-in-sfc
                    return this.createError({
                      message: t(
                        'intelCandidate.form.experience.startDateNotFuture'
                      ),
                    });
                  }
                  return true;
                }
              )
              .typeError(t('intelCandidate.form.experience.invalidStartDate')),
            end: yup
              .mixed()
              .test(
                'skip-if-fresh-grad-or-current',
                t('intelCandidate.form.experience.endDateRequired'),
                // eslint-disable-next-line func-names
                function validateEnd(value) {
                  // eslint-disable-next-line react/no-this-in-sfc
                  const { current } = this.parent;
                  // eslint-disable-next-line react/no-this-in-sfc
                  const rootValue =
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.from && this.from.length > 0
                      ? // eslint-disable-next-line react/no-this-in-sfc
                        this.from[this.from.length - 1]?.value
                      : null;
                  const isFreshGrad =
                    rootValue?.experience?.fresh_grad === true;
                  // Skip validation if fresh_grad is true or current is true
                  if (isFreshGrad || current === true) {
                    return true;
                  }
                  // Otherwise require the date
                  if (!value) return false;
                  // eslint-disable-next-line react/no-this-in-sfc
                  const { start } = this.parent;
                  if (start && new Date(value) < new Date(start)) {
                    // eslint-disable-next-line react/no-this-in-sfc
                    return this.createError({
                      message: t('intelCandidate.form.endDateAfterStart'),
                    });
                  }
                  return true;
                }
              )
              .typeError(t('intelCandidate.form.experience.invalidEndDate')),
            current: yup.boolean(),
            summary: yup.string().nullable(),
          })
        ),
      }),
      education: yup.array().of(
        yup.object({
          degree: yup.object().nullable().required(t('common.cannotBeEmpty')),
          institution: yup.string().required(t('common.cannotBeEmpty')),
          year: yup.mixed().when('ongoing', {
            is: true,
            then: yup.mixed().nullable(),
            otherwise: yup
              .mixed()
              .nullable()
              .required(t('common.cannotBeEmpty')),
          }),
          ongoing: yup.boolean(),
        })
      ),
      skills: yup.object({
        key: yup.array().nullable(),
        certifications: yup.string().nullable(),
        upskill: yup.boolean().nullable(),
      }),
      compensation: yup.object({
        engagement_type: yup.object().required(t('common.cannotBeEmpty')),
        current: yup.object({
          amount: yup.number().nullable().min(0),
          currency: yup.object().nullable(),
          frequency: yup.object().nullable(),
          fx_target_currency: yup.object().nullable(),
          fx_target_frequency: yup.object().nullable(),
          note: yup.string().nullable(),
        }),
        expected: yup.object({
          amount: yup.number().nullable().min(0),
          currency: yup.object().nullable(),
          frequency: yup.object().nullable(),
          fx_target_currency: yup.object().nullable(),
          fx_target_frequency: yup.object().nullable(),
          note: yup.string().nullable(),
        }),
      }),
      availability: yup.object({
        notice_period_days: yup
          .object()
          .nullable()
          .required(t('common.cannotBeEmpty')),
        immediate: yup.boolean(),
        from_date: yup.date().nullable(),
        shift: yup.object().nullable(),
        shift_readiness: yup.object().nullable(),
        tz_overlap: yup.object().nullable(),
        work_mode: yup.object().nullable(),
        preference: yup.object().nullable(),
        travel: yup.object({
          willing: yup.boolean().nullable(),
          percent: yup.object().nullable(),
        }),
        relocate: yup.object({
          willing: yup.boolean().nullable(),
          cities: yup.string().nullable(),
        }),
      }),
      documents: yup.object({
        linkedin: yup
          .string()
          .required(t('common.cannotBeEmpty'))
          .test(
            'is-https',
            t('intelCandidate.form.urlMustStartWithHttps'),
            (value) => {
              if (!value) return true;
              return value.startsWith('https://');
            }
          ),
        github: yup
          .string()
          .nullable()
          .test(
            'is-https',
            t('intelCandidate.form.urlMustStartWithHttps'),
            (value) => {
              if (!value) return true;
              return value.startsWith('https://');
            }
          ),
        portfolio: yup
          .string()
          .nullable()
          .test(
            'is-https',
            t('intelCandidate.form.urlMustStartWithHttps'),
            (value) => {
              if (!value) return true;
              return value.startsWith('https://');
            }
          ),
        resume: yup.mixed().required(t('common.cannotBeEmpty')),
        additional: yup.array().nullable(),
      }),
    });
  }, [t]);

  const requiredFields = useMemo(() => getRequiredFields(), []);

  const buildCareerInfoRequest = async (values) => {
    // Upload resume if prefilled File or wrapper with a File
    let careerResume = null;
    const resumeVal =
      values?.documents?.resume ??
      resumeData?.resumeFile ??
      resumeData?.resumeFile?.file;
    if (resumeVal) {
      if (resumeVal instanceof File) {
        const uploaded = await AdminService.uploadFile(resumeVal);
        careerResume = {
          fileName: uploaded.fileName,
          url: uploaded.uri,
          mimeType: uploaded.mimeType,
          displayName: uploaded.displayName || resumeVal.name,
        };
      } else if (resumeVal?.file instanceof File) {
        const uploaded = await AdminService.uploadFile(resumeVal.file);
        careerResume = {
          fileName: uploaded.fileName,
          url: uploaded.uri,
          mimeType: uploaded.mimeType,
          displayName: uploaded.displayName || resumeVal?.name || 'resume',
        };
      } else if (resumeVal?.url || resumeVal?.uri) {
        careerResume = {
          fileName: resumeVal.fileName || resumeVal.name,
          url: resumeVal.url || resumeVal.uri,
          mimeType: resumeVal.mimeType,
          displayName: resumeVal.displayName || resumeVal.name,
        };
      }
    }

    // Upload additional docs if new Files
    const additionalDocs = Array.isArray(values?.documents?.additional)
      ? values.documents.additional
      : [];

    const uploadedAdditional = await Promise.all(
      additionalDocs.map(async (doc) => {
        if (doc instanceof File) {
          const uploaded = await AdminService.uploadFile(doc);
          return {
            fileName: uploaded.fileName,
            url: uploaded.uri,
            mimeType: uploaded.mimeType,
            displayName: uploaded.displayName || doc.name,
          };
        }
        if (doc?.file instanceof File) {
          const uploaded = await AdminService.uploadFile(doc.file);
          return {
            fileName: uploaded.fileName,
            url: uploaded.uri,
            mimeType: uploaded.mimeType,
            displayName: uploaded.displayName || doc?.name || 'document',
          };
        }
        if (doc?.url || doc?.uri) {
          return {
            fileName: doc.fileName || doc.name,
            url: doc.url || doc.uri,
            mimeType: doc.mimeType,
            displayName: doc.displayName || doc.name,
          };
        }
        return null;
      })
    ).then((arr) => arr.filter(Boolean));

    const workAuthorizations = (values?.workauth || [])
      .filter((wa) => wa?.country && wa?.status)
      .map((wa) => ({
        country: wa.country?.value,
        visaStatusId: wa.status?.value,
        visaStatusDate: wa.expiry || null,
        visaType: wa.visa_type || null,
      }));

    const experiences = (values?.experience?.entries || []).map((e) => ({
      empoyerName: e.employer,
      jobTitle: e.title,
      isCurrent: !!e.current,
      summary: e.summary || null,
      startDate: e.start || null,
      endDate: e.current ? null : e.end || null,
    }));

    const educations = (values?.education || [])
      .filter((ed) => ed?.degree)
      .map((ed) => {
        let graduationYear = null;
        if (!ed.ongoing) {
          graduationYear = ed.year ? Number(ed.year) : null;
        }
        return {
          institution: ed.institution,
          graduationYear,
          graduationId: ed.degree?.value,
          isOngoing: !!ed.ongoing,
        };
      });

    const careerLanguageSelections = (values?.languages || [])
      .filter((lang) => lang?.language && lang?.languageLevel)
      .map((lang) => ({
        languageId: lang.language?.value,
        languageLevelId: lang.languageLevel?.value,
      }));

    const skillIds = (values?.skills?.key || [])
      .map((s) => (typeof s?.value === 'number' ? s.value : null))
      .filter((v) => v != null);

    const current = values?.compensation?.current || {};
    const expected = values?.compensation?.expected || {};
    const availability = values?.availability || {};

    const payload = {
      genderId: values?.personal?.gender?.value || null,
      certifications: values?.skills?.certifications || null,
      firstName: values?.personal?.first_name,
      lastName: values?.personal?.last_name,
      middleName: values?.personal?.middle_name || null,
      country: values?.personal?.country?.value || null,
      city: values?.personal?.city,
      phone: values?.personal?.phone,
      email: values?.personal?.email,
      birthDate: values?.personal?.dob || null,
      source: values?.personal?.source?.value || null,
      externalSource: values?.personal?.externalSource?.value || null,
      nationality:
        (values?.personal?.nationality || [])
          .map((n) => n?.value)
          .filter(Boolean)
          .join(', ') || null,
      willingToUpSkill: values?.skills?.upskill,
      engagementTypeId: values?.compensation?.engagement_type?.value || null,
      isFresher: !!values?.experience?.fresh_grad,
      workAuthorizations,
      careerExperiences: experiences,
      careerEducations: educations,
      careerLanguageSelections,
      skillIds,
      currentSalary: current?.amount ? Number(current.amount) : null,
      currentSalaryCurrencyCode: current?.currency?.value || null,
      currentSalaryFrequency: current?.frequency?.value || null,
      currentSalaryInfo: current?.note || null,
      expectedSalary: expected?.amount ? Number(expected.amount) : null,
      expectedSalaryCurrencyCode: expected?.currency?.value || null,
      exptecedSalaryFrequency: expected?.frequency?.value || null,
      expectedSalaryInfo: expected?.note || null,
      noticePeriodId: availability?.notice_period_days?.value || null,
      availableFrom: availability?.from_date || null,
      workShiftId: availability?.shift?.value || null,
      timeZoneOverlap: availability?.tz_overlap?.value || null,
      workShiftReadinessId: availability?.shift_readiness?.value || null,
      jobLocationTypeId: availability?.work_mode?.value || null,
      engagementPreferenceId: availability?.preference?.value || null,
      willingToTravel: availability?.travel?.willing,
      travelPercentage: availability?.travel?.percent?.value || null,
      travelCities: availability?.relocate?.cities || null,
      willingToRelocate: availability?.relocate?.willing,
      immediateJoiner: availability?.immediate || false,
      careerOtherDocuments: uploadedAdditional,
      careerResume,
      linkedIn: values?.documents?.linkedin || null,
      github: values?.documents?.github || null,
      portfolio: values?.documents?.portfolio || null,
    };

    return payload;
  };

  // Handle scroll-triggered section detection
  useEffect(() => {
    const handleScroll = () => {
      if (!formContentRef.current) return;

      const container = formContentRef.current;
      const sections = PRIMARY_TABS.map((section) => ({
        name: section,
        element: document.getElementById(`section-${section}`),
      })).filter((s) => s.element);

      const containerRect = container.getBoundingClientRect();

      // Find which section is currently closest to the top
      let activeSectionName = sections[0]?.name || 'Personal';
      let minDistance = Infinity;

      sections.forEach(({ name, element }) => {
        const elementRect = element.getBoundingClientRect();
        const relativeTop = elementRect.top - containerRect.top;
        const distance = Math.abs(relativeTop - 40); // 40px is our target offset

        if (
          distance < minDistance &&
          relativeTop >= -element.offsetHeight / 2
        ) {
          minDistance = distance;
          activeSectionName = name;
        }
      });

      setActiveSection(activeSectionName);
    };

    const formContent = formContentRef.current;
    if (formContent) {
      formContent.addEventListener('scroll', handleScroll);
      return () => formContent.removeEventListener('scroll', handleScroll);
    }
    return undefined;
  }, []);

  // Track form value changes and notify parent component
  useEffect(() => {
    let isStabilized = false;
    let capturedBaseline = null;

    // Wait for form to stabilize (all default values set by components)
    const stabilizationTimer = setTimeout(() => {
      if (formikRef.current) {
        // Capture the actual form values after stabilization
        // This accounts for any component-level defaults that might differ from initialValues
        capturedBaseline = JSON.parse(JSON.stringify(formikRef.current.values));
        isStabilized = true;
      }
    }, 1000); // Wait 1 second for form to settle

    const checkFormChanges = () => {
      if (!formikRef.current || !onFormValuesChange || !isStabilized) {
        return;
      }

      const { values } = formikRef.current;

      // Use captured baseline if available, otherwise use computed baseline
      const comparisonBaseline = capturedBaseline || baselineValues;

      // Normalize values for comparison (handle Date objects, etc.)
      const normalizedCurrent = JSON.parse(JSON.stringify(values));
      const normalizedBaseline = JSON.parse(JSON.stringify(comparisonBaseline));

      const hasChanges = !deepEqual(normalizedBaseline, normalizedCurrent);

      // Scenario: Resume parsed - always show popup since values are prefilled
      // Set to true after form stabilizes to indicate prefilled values exist
      if (resumeData && !candidate) {
        // Always show popup for resume parsed scenario (values are prefilled)
        onFormValuesChange(true);
        return;
      }

      // Scenario: Empty form - compare with default values
      // Only show popup if there are actual changes from defaults
      // Scenario: Edit mode - compare with prefilled candidate values
      // Only show popup if current state differs from initial prefilled state
      onFormValuesChange(hasChanges);
    };

    // Check periodically for value changes
    const interval = setInterval(checkFormChanges, 200);

    return () => {
      clearTimeout(stabilizationTimer);
      clearInterval(interval);
    };
  }, [onFormValuesChange, baselineValues, resumeData, candidate]);

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValuesWithResume}
      validationSchema={validationSchema}
      validateOnMount={!!resumeData}
      validateOnBlur
      validateOnChange={hasAttemptedSubmit}
      onSubmit={() => {}}
    >
      {(formik) => {
        const { values, errors, touched, setFieldValue, setFieldTouched } =
          formik;

        const validRequiredFieldsCount = requiredFields.reduce((acc, path) => {
          const segments = path.split('.');
          let fieldValue = values;
          let fieldError = errors;
          let fieldTouched = touched;

          // Navigate through the nested object structure
          // eslint-disable-next-line no-restricted-syntax
          for (const segment of segments) {
            fieldValue = fieldValue?.[segment];
            fieldError = fieldError?.[segment];
            fieldTouched = fieldTouched?.[segment];
          }

          // Check if field has a value
          // Determine value based on type
          let hasValue = false;
          if (Array.isArray(fieldValue)) {
            hasValue = fieldValue.length > 0;
          } else if (fieldValue && typeof fieldValue === 'object') {
            // Support dropdown-like objects with value, and file-like objects with name/displayName
            hasValue =
              !!fieldValue.value ||
              !!fieldValue.name ||
              !!fieldValue.displayName;
          } else if (typeof fieldValue === 'string') {
            hasValue = path.includes('phone')
              ? fieldValue.trim().length > 5
              : fieldValue.trim().length > 0;
          }

          // Check if field is valid (has value and no validation errors)
          const isValid = hasValue && !fieldError;
          // debug: isValid for field
          return isValid ? acc + 1 : acc;
        }, 0);
        // debug: validRequiredFieldsCount
        const totalRequiredFields = requiredFields.length;
        const percent =
          Math.round((validRequiredFieldsCount / totalRequiredFields) * 100) ||
          0;
        const allRequiredFieldsCompleted =
          validRequiredFieldsCount === totalRequiredFields;
        // debug: requiredFieldsCount

        let progressColor = classes.progressRed;
        if (percent >= 80) progressColor = classes.progressGreen;
        else if (percent >= 50) progressColor = classes.progressOrange;

        // Notify parent component of progress changes
        if (onProgressChange) {
          const currentProgress = { percent, progressColor };
          if (
            !prevProgressRef.current ||
            prevProgressRef.current.percent !== percent ||
            prevProgressRef.current.progressColor !== progressColor
          ) {
            prevProgressRef.current = currentProgress;
            onProgressChange(currentProgress);
          }
        }

        const getSectionStatus = (section) => {
          // Personal section status logic
          if (section === 'Personal') {
            const requiredPersonalFields = [
              'first_name',
              'last_name',
              'email',
              'phone',
              'country',
              'city',
            ];

            const personalErrors = formik.errors.personal;
            // Check if any required field has an error
            if (personalErrors && formik.touched.personal) return 'error';

            const hasPendingFields = requiredPersonalFields.some(
              (field) => !formik.values.personal[field]
            );

            return hasPendingFields ? 'pending' : 'complete';
          }

          if (section === 'Documents') {
            const requiredDocumentsFields = ['resume', 'linkedin'];
            const documentsErrors = formik.errors.documents;
            // Check if any required field has an error
            if (documentsErrors && formik.touched.documents) return 'error';

            const hasPendingFields = requiredDocumentsFields.some(
              (field) => !formik.values.documents[field]
            );

            return hasPendingFields ? 'pending' : 'complete';
          }

          if (section === 'Skills') {
            const requiredSkillsFields = ['key'];
            const skillsErrors = formik.errors.skills;

            const hasPendingFields = requiredSkillsFields.some(
              (field) => formik.values.skills[field].length === 0
            );

            // Check if any required field has an error
            if ((skillsErrors || hasPendingFields) && formik.touched.skills)
              return 'error';

            return hasPendingFields ? 'pending' : 'complete';
          }

          if (section === 'Comp & Availability') {
            const requiredCompAvailabilityFields = ['notice_period_days'];
            const compAvailabilityErrors = formik.errors.availability;
            // Check if any required field has an error
            if (compAvailabilityErrors && formik.touched.availability)
              return 'error';

            const hasPendingFields = requiredCompAvailabilityFields.some(
              (field) => !formik.values.availability[field]
            );

            return hasPendingFields ? 'pending' : 'complete';
          }

          // Other sections are incomplete by default for now
          return 'incomplete';
        };

        // const getSectionStatus = (section) => {
        //   // Personal section status logic
        //   if (section === 'Personal') {
        //     const requiredPersonalFields = [
        //       'first_name',
        //       'last_name',
        //       'email',
        //       'phone',
        //       'country',
        //       'city',
        //     ];
        //     const personalErrors = formik.errors.personal;
        //     console.log('formik', formik);
        //     const personalTouched = formik.personal;

        //     // Check if any required field has an error
        //     const hasErrors = requiredPersonalFields.some(
        //       (field) => personalErrors?.[field] && personalTouched?.[field]
        //     );
        //     if (hasErrors) return 'error';

        //     // Check if all required fields are filled
        //     const allRequiredFilled = requiredPersonalFields.every((field) => {
        //       const value = formik.values.personal[field];
        //       return (
        //         value &&
        //         ((typeof value === 'object' && value.value) ||
        //           (typeof value === 'string' && value.trim().length > 0))
        //       );
        //     });

        //     if (allRequiredFilled) return 'complete';

        //     // Check if any required field is touched but not filled
        //     const hasPendingFields = requiredPersonalFields.some(
        //       (field) =>
        //         personalTouched?.[field] && !formik.values.personal[field]
        //     );
        //     if (hasPendingFields) return 'pending';
        //   }

        //   if (section === 'Documents') {
        //     const requiredDocumentsFields = ['resume', 'linkedin'];
        //     const documentsErrors = formik.errors.documents;
        //     const documentsTouched = formik.touched.documents;

        //     // Check if any required field has an error
        //     const hasErrors = requiredDocumentsFields.some(
        //       (field) => documentsErrors?.[field] && documentsTouched?.[field]
        //     );
        //     if (hasErrors) return 'error';

        //     // Check if all required fields are filled
        //     const allRequiredFilled = requiredDocumentsFields.every((field) => {
        //       const value = formik.values.documents[field];
        //       return (
        //         value &&
        //         ((typeof value === 'object' && value) ||
        //           (typeof value === 'string' && value.trim().length > 0))
        //       );
        //     });

        //     if (allRequiredFilled) return 'complete';

        //     // Check if any required field is touched but not filled
        //     const hasPendingFields = requiredDocumentsFields.some(
        //       (field) =>
        //         documentsTouched?.[field] && !formik.values.documents[field]
        //     );
        //     if (hasPendingFields) return 'pending';
        //   }

        //   if (section === 'Skills') {
        //     const requiredExperienceFields = ['key'];
        //     const experienceErrors = formik.errors.skills;
        //     const experienceTouched = formik.touched.skills;

        //     // Check if any required field has an error
        //     const hasErrors = requiredExperienceFields.some(
        //       (field) => experienceErrors?.[field] && experienceTouched?.[field]
        //     );
        //     if (hasErrors) return 'error';

        //     // Check if all required fields are filled
        //     const allRequiredFilled = requiredExperienceFields.every(
        //       (field) => {
        //         const value = formik.values.skills[field];
        //         return (
        //           value &&
        //           ((typeof value === 'object' && value.value) ||
        //             (Array.isArray(value) && value.length > 0) ||
        //             (typeof value === 'string' && value.trim().length > 0))
        //         );
        //       }
        //     );

        //     if (allRequiredFilled) return 'complete';

        //     // Check if any required field is touched but not filled
        //     const hasPendingFields = requiredExperienceFields.some((field) => {
        //       const value = formik.values.skills[field];
        //       return (
        //         experienceTouched?.[field] &&
        //         (!value || (Array.isArray(value) && value.length === 0))
        //       );
        //     });
        //     if (hasPendingFields) return 'pending';
        //   }

        //   // Other sections are incomplete by default for now
        //   return 'incomplete';
        // };

        const handleSectionClick = (section) => {
          setActiveSection(section);
          const sectionElement = document.getElementById(`section-${section}`);
          if (sectionElement && formContentRef.current) {
            const container = formContentRef.current;
            const containerRect = container.getBoundingClientRect();
            const elementRect = sectionElement.getBoundingClientRect();
            const relativeTop = elementRect.top - containerRect.top;
            const currentScrollTop = container.scrollTop;
            const targetScrollTop = currentScrollTop + relativeTop - 40; // 40px offset from top

            container.scrollTo({
              top: Math.max(0, targetScrollTop),
              behavior: 'smooth',
            });
          }
        };

        const markErrorsTouched = (errorsObj, pathPrefix = '') => {
          if (!errorsObj || typeof errorsObj !== 'object') return;
          Object.entries(errorsObj).forEach(([key, val]) => {
            const path = pathPrefix ? `${pathPrefix}.${key}` : key;
            if (val && typeof val === 'object' && !Array.isArray(val)) {
              markErrorsTouched(val, path);
            } else if (Array.isArray(val)) {
              val.forEach((item, index) => {
                const arrayPath = `${path}.${index}`;
                if (item && typeof item === 'object') {
                  markErrorsTouched(item, arrayPath);
                } else {
                  formik.setFieldTouched(arrayPath, true, false);
                }
              });
            } else {
              formik.setFieldTouched(path, true, false);
            }
          });
        };

        const scrollToFirstError = (errorsObj) => {
          if (!errorsObj || typeof errorsObj !== 'object') return;

          // Map field paths to their section names
          const fieldToSectionMap = {
            personal: 'Personal',
            workauth: 'Work Auth',
            languages: 'Languages',
            experience: 'Experience',
            education: 'Education',
            skills: 'Skills',
            compensation: 'Comp & Availability',
            availability: 'Comp & Availability',
            documents: 'Documents',
          };

          // Find first error field path
          const findFirstErrorPath = (obj, prefix = '') => {
            const entries = Object.entries(obj);
            // eslint-disable-next-line no-restricted-syntax
            for (let idx = 0; idx < entries.length; idx += 1) {
              const [key, value] = entries[idx];
              const path = prefix ? `${prefix}.${key}` : key;
              if (value && typeof value === 'object' && !Array.isArray(value)) {
                const nested = findFirstErrorPath(value, path);
                if (nested) return nested;
              } else if (Array.isArray(value)) {
                const arrayResult = value
                  .map((item, index) => {
                    if (item) {
                      return findFirstErrorPath(item, `${path}.${index}`);
                    }
                    return null;
                  })
                  .find((result) => result !== null);
                if (arrayResult) return arrayResult;
              } else if (value) {
                return path;
              }
            }
            return null;
          };

          const firstErrorPath = findFirstErrorPath(errorsObj);
          if (!firstErrorPath) return;

          // Extract section name from path
          const sectionName = fieldToSectionMap[firstErrorPath.split('.')[0]];
          if (!sectionName) return;

          // Scroll to the section
          const sectionElement = document.getElementById(
            `section-${sectionName}`
          );
          if (sectionElement && formContentRef.current) {
            const container = formContentRef.current;
            const containerRect = container.getBoundingClientRect();
            const elementRect = sectionElement.getBoundingClientRect();
            const relativeTop = elementRect.top - containerRect.top;
            const currentScrollTop = container.scrollTop;
            const targetScrollTop = currentScrollTop + relativeTop - 40; // 40px offset from top

            container.scrollTo({
              top: Math.max(0, targetScrollTop),
              behavior: 'smooth',
            });
          }
        };

        const handleSave = async () => {
          setHasAttemptedSubmit(true);

          const validationErrors = await formik.validateForm();
          const hasValidationErrors =
            validationErrors && Object.keys(validationErrors).length > 0;
          if (hasValidationErrors) {
            markErrorsTouched(validationErrors);
            // Scroll to first error
            scrollToFirstError(validationErrors);
            return;
          }
          if (!allRequiredFieldsCompleted) return;
          try {
            setIsCreatingOrUpdating(true);
            setIsFetching(true);

            const request = await buildCareerInfoRequest(formik.values);
            if (isEditMode) {
              await AdminService.updateCareerInfo(candidate.id, request);
              setIsFetching(false);
              showModal({
                title: 'Updated',
                text: 'Candidate has been updated.',
                onCancel: () => {
                  refetchCandidates?.();
                },
              });
            } else {
              await AdminService.createCareerInfo(request);
              setIsFetching(false);
              onAdded?.();
            }
          } catch (error) {
            showUnknownErrorModal();
          } finally {
            setIsCreatingOrUpdating(false);
            setIsFetching(false);
          }
        };

        const handleSubmit = async () => {
          if (isEditMode) return; // hidden in edit mode

          setHasAttemptedSubmit(true);
          const validationErrors = await formik.validateForm();
          const hasValidationErrors =
            validationErrors && Object.keys(validationErrors).length > 0;
          if (hasValidationErrors) {
            markErrorsTouched(validationErrors);
            return;
          }
          if (!allRequiredFieldsCompleted) return;
          try {
            setIsCreatingOrUpdating(true);
            setIsFetching(true);
            const request = await buildCareerInfoRequest(formik.values);
            await AdminService.createCareerInfo(request);
            setIsFetching(false);
            onAdded?.();
          } catch (error) {
            showUnknownErrorModal();
          } finally {
            setIsCreatingOrUpdating(false);
            setIsFetching(false);
          }
        };

        // Helper function to determine if field should show errors
        const shouldShowError = (fieldPath) => {
          const pathSegments = fieldPath.split('.');
          let touchedValue = touched;
          // eslint-disable-next-line no-restricted-syntax
          for (const segment of pathSegments) {
            touchedValue = touchedValue?.[segment];
          }
          return touchedValue || hasAttemptedSubmit;
        };

        // Prefilled highlight helpers (resume-driven values until touched)
        const getValueAtPath = (obj, fieldPath) => {
          const segments = fieldPath.split('.');
          let cur = obj;
          // eslint-disable-next-line no-restricted-syntax
          for (const seg of segments) {
            cur = cur?.[seg];
          }
          return cur;
        };

        const isFieldTouchedStrict = (fieldPath) => {
          const segments = fieldPath.split('.');
          let cur = touched;
          // eslint-disable-next-line no-restricted-syntax
          for (const seg of segments) {
            cur = cur?.[seg];
          }
          return !!cur;
        };

        const isEmptyValue = (v) => v === '' || v === null || v === undefined;

        const isPrefilledFromResume = (fieldPath) => {
          if (!resumeData) return false;
          if (isFieldTouchedStrict(fieldPath)) return false;
          const withResume = getValueAtPath(initialValuesWithResume, fieldPath);
          const withoutResume = getValueAtPath(
            initialValuesWithoutResume,
            fieldPath
          );
          const currentVal = getValueAtPath(values, fieldPath);
          return (
            isEmptyValue(withoutResume) &&
            !isEmptyValue(withResume) &&
            currentVal === withResume
          );
        };

        const currentFxValue = computeFxEquivalent(values.compensation.current);
        const expectedFxValue = computeFxEquivalent(
          values.compensation.expected
        );
        const currentTargetCode =
          values?.compensation?.current?.fx_target_currency?.value || 'USD';
        const expectedTargetCode =
          values?.compensation?.expected?.fx_target_currency?.value || 'USD';

        return (
          <div className={classes.CandidateForm}>
            <div className={classes.topBar}>
              <SectionNavigator
                sections={PRIMARY_TABS}
                activeSection={activeSection}
                setActiveSection={handleSectionClick}
                translationPath="intelCandidate.form.toptabs"
                getSectionStatus={getSectionStatus}
              />
            </div>
            <div className={classes.formContent} ref={formContentRef}>
              {/* Personal Section */}
              <div id="section-Personal" className={classes.section}>
                <h2 className={classes.sectionTitle}>
                  {t('intelCandidate.form.tabs.Personal')}
                </h2>
                <div className={`${classes.row} ${classes.cols3}`}>
                  <TextInput
                    name="personal.first_name"
                    label={t('intelCandidate.form.personal.firstName')}
                    placeholder={t(
                      'intelCandidate.form.placeholders.firstName'
                    )}
                    value={values.personal.first_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={errors.personal?.first_name}
                    touched={shouldShowError('personal.first_name')}
                    prefilled={isPrefilledFromResume('personal.first_name')}
                    height={45}
                  />
                  <TextInput
                    name="personal.middle_name"
                    label={t('intelCandidate.form.personal.middleName')}
                    placeholder={t(
                      'intelCandidate.form.placeholders.middleName'
                    )}
                    value={values.personal.middle_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={errors.personal?.middle_name}
                    touched={touched.personal?.middle_name}
                    prefilled={isPrefilledFromResume('personal.middle_name')}
                    height={45}
                    note={t('common.optional')}
                  />
                  <TextInput
                    name="personal.last_name"
                    label={t('intelCandidate.form.personal.lastName')}
                    placeholder={t('intelCandidate.form.placeholders.lastName')}
                    value={values.personal.last_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={errors.personal?.last_name}
                    touched={touched.personal?.last_name}
                    prefilled={isPrefilledFromResume('personal.last_name')}
                    height={45}
                  />
                </div>

                <div className={`${classes.row} ${classes.cols3}`}>
                  <TextInput
                    name="personal.email"
                    label={t('intelCandidate.form.personal.email')}
                    placeholder="name@domain.ext"
                    value={values.personal.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={errors.personal?.email}
                    touched={touched.personal?.email}
                    prefilled={isPrefilledFromResume('personal.email')}
                    height={45}
                    type="email"
                  />
                  <PhoneInput
                    name="personal.phone"
                    label={t('intelCandidate.form.personal.phone')}
                    placeholder="+1 555 555 5555"
                    setFieldValue={setFieldValue}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    touched={touched.personal?.phone}
                    error={errors.personal?.phone}
                    value={values.personal.phone}
                    height={45}
                  />
                  <FormDropdown
                    name="personal.country"
                    label={t('intelCandidate.form.personal.country')}
                    placeholder={t('intelCandidate.form.placeholders.country')}
                    value={values.personal.country}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    touched={touched.personal?.country}
                    error={errors.personal?.country}
                    options={COUNTRY_OPTIONS}
                    height={45}
                  />
                </div>

                <div className={`${classes.row} ${classes.cols4}`}>
                  <TextInput
                    name="personal.city"
                    label={t('intelCandidate.form.personal.city')}
                    placeholder={t('intelCandidate.form.placeholders.city')}
                    value={values.personal.city}
                    onChange={(e) => {
                      // Prevent numeric input for city
                      const filtered = e.target.value.replace(/[0-9]/g, '');
                      setFieldValue('personal.city', filtered);
                    }}
                    onBlur={formik.handleBlur}
                    error={errors.personal?.city}
                    touched={touched.personal?.city}
                    prefilled={isPrefilledFromResume('personal.city')}
                    height={45}
                  />
                  <div className={classes.datePickerWrapper}>
                    <DatePicker
                      name="personal.dob"
                      label={t('intelCandidate.form.personal.dob')}
                      value={values.personal.dob}
                      setFieldValue={setFieldValue}
                      setFieldTouched={setFieldTouched}
                      touched={touched.personal?.dob}
                      error={errors.personal?.dob}
                      note={t('common.optional')}
                      height={45}
                      newCalendar
                      onlyPastDates
                      clearable
                    />
                  </div>
                  <FormDropdown
                    name="personal.gender"
                    label={t('intelCandidate.form.personal.gender')}
                    placeholder={t('intelCandidate.form.placeholders.gender')}
                    value={values.personal.gender}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    touched={touched.personal?.gender}
                    error={errors.personal?.gender}
                    fetchOptions={{
                      dataName: 'gendersOptionsBasicInfo',
                      getDataHandler: ({ pageNumber, term }) =>
                        AdminService.getGenders({
                          orderBy: 'sortOrder',
                          sortOrder: 'asc',
                          term,
                          pageNumber,
                        }),
                    }}
                    note={t('common.optional')}
                    height={45}
                  />
                  <MultiDropdown
                    name="personal.nationality"
                    label={t('intelCandidate.form.personal.nationality')}
                    placeholder={t(
                      'intelCandidate.form.placeholders.nationality'
                    )}
                    value={values.personal.nationality}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    touched={touched.personal?.nationality}
                    error={errors.personal?.nationality}
                    options={COUNTRY_OPTIONS}
                    note={t('common.optional')}
                    height={45}
                    maxLength={5}
                  />
                </div>
                <div className={`${classes.row} ${classes.cols2}`}>
                  <FormDropdown
                    name="personal.source"
                    label={t('intelCandidate.form.personal.source')}
                    placeholder={t('intelCandidate.form.placeholders.source')}
                    value={values.personal.source}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    options={sourceOptions}
                    height={45}
                  />
                  {values.personal.source?.value === 'External Source' && (
                    <FormDropdown
                      name="personal.externalSource"
                      label={t('intelCandidate.form.personal.externalSource')}
                      placeholder={t(
                        'intelCandidate.form.placeholders.externalSource'
                      )}
                      value={values.personal.externalSource}
                      setFieldValue={setFieldValue}
                      setFieldTouched={setFieldTouched}
                      options={externalSourceOptions}
                      height={45}
                      note={t('common.optional')}
                    />
                  )}
                </div>
              </div>

              {/* Work Authorization Section */}
              <div id="section-Work Auth" className={classes.section}>
                <div className={classes.sectionHeader}>
                  <h2 className={classes.sectionTitle}>
                    {t('intelCandidate.form.tabs.Work Auth')}
                  </h2>
                  <Button
                    variant="blackWhite"
                    height={45}
                    onClick={() => {
                      const newAuth = [
                        ...values.workauth,
                        {
                          id: generateId(),
                          country: null,
                          status: null,
                          visa_type: '',
                          expiry: '',
                        },
                      ];
                      setFieldValue('workauth', newAuth);
                    }}
                  >
                    + {t('intelCandidate.form.workAuth.addAuthorization')}
                  </Button>
                </div>
                {values.workauth.length === 0 ? (
                  <div className={classes.emptyState}>
                    <span className={classes.emptyStateText}>
                      {t('intelCandidate.form.workAuth.emptyState')}
                    </span>
                  </div>
                ) : (
                  values.workauth.map((auth, index) => (
                    <div
                      key={`workauth-${auth.id || index}`}
                      className={classes.repeaterCard}
                    >
                      <div className={`${classes.row} ${classes.cols4}`}>
                        <FormDropdown
                          name={`workauth.${index}.country`}
                          label={t('intelCandidate.form.workAuth.country')}
                          placeholder="Select Country"
                          value={auth.country}
                          setFieldValue={setFieldValue}
                          setFieldTouched={setFieldTouched}
                          options={COUNTRY_OPTIONS}
                          error={errors.workauth?.[index]?.country}
                          touched={touched.workauth?.[index]?.country}
                          height={45}
                        />
                        <FormDropdown
                          name={`workauth.${index}.status`}
                          label={t('intelCandidate.form.workAuth.status')}
                          placeholder="Select Status"
                          value={auth.status}
                          setFieldValue={setFieldValue}
                          setFieldTouched={setFieldTouched}
                          error={errors.workauth?.[index]?.status}
                          touched={touched.workauth?.[index]?.status}
                          fetchOptions={{
                            dataName: 'workAuthStatusesOptionsBasicInfo',
                            getDataHandler: ({ pageNumber, term }) =>
                              AdminService.getWorkAuthStatuses({
                                orderBy: 'sortOrder',
                                sortOrder: 'asc',
                                term,
                                pageNumber,
                              }),
                          }}
                          height={45}
                        />
                        <TextInput
                          name={`workauth.${index}.visa_type`}
                          label={t('intelCandidate.form.workAuth.visaType')}
                          placeholder="Enter Visa Type"
                          value={auth.visa_type}
                          onChange={formik.handleChange}
                          error={errors.workauth?.[index]?.visa_type}
                          touched={touched.workauth?.[index]?.visa_type}
                          onBlur={formik.handleBlur}
                          prefilled={isPrefilledFromResume(
                            `workauth.${index}.visa_type`
                          )}
                          height={45}
                          note={t('common.optional')}
                        />
                        <DatePicker
                          name={`workauth.${index}.expiry`}
                          label={t('intelCandidate.form.workAuth.expiration')}
                          value={auth.expiry}
                          setFieldValue={setFieldValue}
                          setFieldTouched={setFieldTouched}
                          error={errors.workauth?.[index]?.expiry}
                          touched={touched.workauth?.[index]?.expiry}
                          height={45}
                          newCalendar
                        />
                      </div>
                      <div className={`${classes.row} ${classes.cols1}`}>
                        <div className={classes.deleteButtonWrapper}>
                          <button
                            type="button"
                            className={classes.deleteButton}
                            onClick={() => {
                              const newAuth = [...values.workauth];
                              newAuth.splice(index, 1);
                              setFieldValue('workauth', newAuth);
                              setFieldTouched('workauth', true, false);
                            }}
                          >
                            {t('common.delete')}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Languages Section */}
              <div id="section-Languages" className={classes.section}>
                <div className={classes.sectionHeader}>
                  <h2 className={classes.sectionTitle}>
                    {t('intelCandidate.form.tabs.Languages')}
                  </h2>
                  <Button
                    variant="blackWhite"
                    height={45}
                    onClick={() => {
                      const newLanguages = [
                        ...values.languages,
                        {
                          id: generateId(),
                          language: null,
                          languageLevel: null,
                        },
                      ];
                      setFieldValue('languages', newLanguages);
                    }}
                  >
                    + {t('intelCandidate.form.languages.addLanguage')}
                  </Button>
                </div>
                {values.languages.length === 0 ? (
                  <div className={classes.emptyState}>
                    <span className={classes.emptyStateText}>
                      {t('intelCandidate.form.languages.emptyState')}
                    </span>
                  </div>
                ) : (
                  values.languages.map((lang, index) => (
                    <div
                      key={`language-${lang.id || index}`}
                      className={classes.repeaterCard}
                    >
                      <div className={`${classes.row} ${classes.cols2}`}>
                        <FormDropdown
                          name={`languages.${index}.language`}
                          label={t('intelCandidate.form.languages.language')}
                          placeholder={t(
                            'intelCandidate.form.languages.selectLanguage'
                          )}
                          value={lang.language}
                          setFieldValue={setFieldValue}
                          setFieldTouched={setFieldTouched}
                          error={errors.languages?.[index]?.language}
                          touched={touched.languages?.[index]?.language}
                          fetchOptions={{
                            dataName: 'languageOptionsBasicInfo',
                            getDataHandler: ({ pageNumber, term }) =>
                              AdminService.getLanguages({
                                orderBy: 'sortOrder',
                                sortOrder: 'asc',
                                term,
                                pageNumber,
                              }),
                          }}
                          height={45}
                        />
                        <FormDropdown
                          name={`languages.${index}.languageLevel`}
                          label={t('intelCandidate.form.languages.level')}
                          placeholder={t(
                            'intelCandidate.form.languages.selectLevel'
                          )}
                          value={lang.languageLevel}
                          setFieldValue={setFieldValue}
                          setFieldTouched={setFieldTouched}
                          error={errors.languages?.[index]?.languageLevel}
                          touched={touched.languages?.[index]?.languageLevel}
                          fetchOptions={{
                            dataName: 'languageLevelOptionsBasicInfo',
                            getDataHandler: ({ pageNumber, term }) =>
                              AdminService.getLanguageLevels({
                                orderBy: 'sortOrder',
                                sortOrder: 'asc',
                                term,
                                pageNumber,
                              }),
                          }}
                          height={45}
                        />
                      </div>
                      <div className={`${classes.row} ${classes.cols1}`}>
                        <div className={classes.deleteButtonWrapper}>
                          <button
                            type="button"
                            className={classes.deleteButton}
                            onClick={() => {
                              const newLanguages = [...values.languages];
                              newLanguages.splice(index, 1);
                              setFieldValue('languages', newLanguages);
                              setFieldTouched('languages', true, false);
                            }}
                          >
                            {t('common.delete')}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Experience Section */}
              <div id="section-Experience" className={classes.section}>
                <div className={classes.experienceHeader}>
                  <div className={classes.experienceLeft}>
                    <h2 className={classes.sectionTitle}>
                      {t('intelCandidate.form.tabs.Experience')}
                    </h2>
                    <div className={classes.toggleGroup}>
                      <Switch
                        label={t(
                          'intelCandidate.form.experience.freshGraduate'
                        )}
                        isEnabled={values.experience.fresh_grad}
                        onClick={() =>
                          setFieldValue(
                            'experience.fresh_grad',
                            !values.experience.fresh_grad
                          )
                        }
                      />
                    </div>
                  </div>
                  {!values.experience.fresh_grad && (
                    <Button
                      variant="blackWhite"
                      height={45}
                      onClick={() => {
                        const newEntries = [
                          ...values.experience.entries,
                          {
                            id: generateId(),
                            employer: '',
                            title: '',
                            start: '',
                            end: '',
                            current: false,
                            summary: '',
                          },
                        ];
                        setFieldValue('experience.entries', newEntries);
                      }}
                    >
                      + {t('intelCandidate.form.experience.addExperience')}
                    </Button>
                  )}
                </div>

                {!values.experience.fresh_grad && (
                  <>
                    {values.experience.entries.length === 0 ? (
                      <div className={classes.emptyState}>
                        <span className={classes.emptyStateText}>
                          {t('intelCandidate.form.experience.emptyState')}
                        </span>
                      </div>
                    ) : (
                      values.experience.entries.map((exp, index) => (
                        <div
                          key={`experience-${exp.id || index}`}
                          className={classes.repeaterCard}
                        >
                          <div className={`${classes.row} ${classes.cols4}`}>
                            <TextInput
                              name={`experience.entries.${index}.employer`}
                              label={t(
                                'intelCandidate.form.experience.employer'
                              )}
                              placeholder="Enter Employer Name"
                              value={exp.employer}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              error={
                                errors.experience?.entries?.[index]?.employer
                              }
                              touched={
                                touched.experience?.entries?.[index]?.employer
                              }
                              setFieldTouched={setFieldTouched}
                              setFieldValue={setFieldValue}
                              prefilled={isPrefilledFromResume(
                                `experience.entries.${index}.employer`
                              )}
                              height={45}
                            />
                            <TextInput
                              name={`experience.entries.${index}.title`}
                              label={t('intelCandidate.form.experience.title')}
                              placeholder="Enter Title"
                              value={exp.title}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              error={errors.experience?.entries?.[index]?.title}
                              touched={
                                touched.experience?.entries?.[index]?.title
                              }
                              setFieldTouched={setFieldTouched}
                              setFieldValue={setFieldValue}
                              prefilled={isPrefilledFromResume(
                                `experience.entries.${index}.title`
                              )}
                              height={45}
                            />
                            <div className={classes.datePickerWrapper}>
                              <DatePicker
                                name={`experience.entries.${index}.start`}
                                label={t(
                                  'intelCandidate.form.experience.startDate'
                                )}
                                value={exp.start}
                                setFieldValue={setFieldValue}
                                setFieldTouched={setFieldTouched}
                                height={45}
                                error={
                                  errors.experience?.entries?.[index]?.start
                                }
                                touched={
                                  touched.experience?.entries?.[index]?.start
                                }
                                newCalendar
                                onlyPastDates
                              />
                            </div>
                            <div className={classes.endDateGroup}>
                              <DatePickerSwitch
                                name={`experience.entries.${index}.end`}
                                label={t(
                                  'intelCandidate.form.experience.endDate'
                                )}
                                value={exp.end}
                                setFieldValue={setFieldValue}
                                setFieldTouched={setFieldTouched}
                                error={errors.experience?.entries?.[index]?.end}
                                touched={
                                  touched.experience?.entries?.[index]?.end
                                }
                                height={45}
                                newCalendar
                                readonly={exp.current}
                                switchName={`experience.entries.${index}.current`}
                                switchValue={exp.current}
                                switchLabel={t(
                                  'intelCandidate.form.experience.currentJob'
                                )}
                                onSwitchChange={(v) => {
                                  setFieldValue(
                                    `experience.entries.${index}.current`,
                                    v
                                  );
                                  if (v === true) {
                                    setFieldValue(
                                      `experience.entries.${index}.end`,
                                      ''
                                    );
                                  }
                                }}
                              />
                            </div>
                          </div>
                          <div className={`${classes.row} ${classes.cols1}`}>
                            <Textarea
                              name={`experience.entries.${index}.summary`}
                              label={t(
                                'intelCandidate.form.experience.summary'
                              )}
                              placeholder="Enter brief summary"
                              value={exp.summary}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              height={100}
                              error={
                                errors.experience?.entries?.[index]?.summary
                              }
                              touched={
                                touched.experience?.entries?.[index]?.summary
                              }
                              setFieldTouched={setFieldTouched}
                              setFieldValue={setFieldValue}
                              prefilled={isPrefilledFromResume(
                                `experience.entries.${index}.summary`
                              )}
                              maxLength={1000}
                              note={t('common.optional')}
                            />
                          </div>
                          <div className={`${classes.row} ${classes.cols1}`}>
                            <div className={classes.deleteButtonWrapper}>
                              <button
                                type="button"
                                className={classes.deleteButton}
                                onClick={() => {
                                  const newEntries = [
                                    ...values.experience.entries,
                                  ];
                                  newEntries.splice(index, 1);
                                  setFieldValue(
                                    'experience.entries',
                                    newEntries
                                  );
                                  setFieldTouched(
                                    'experience.entries',
                                    true,
                                    false
                                  );
                                }}
                              >
                                {t('common.delete')}
                              </button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </>
                )}
              </div>

              {/* Education Section */}
              <div id="section-Education" className={classes.section}>
                <div className={classes.sectionHeader}>
                  <h2 className={classes.sectionTitle}>
                    {t('intelCandidate.form.tabs.Education')}
                  </h2>
                  <Button
                    variant="blackWhite"
                    height={45}
                    onClick={() => {
                      const newEducation = [
                        ...values.education,
                        {
                          id: generateId(),
                          degree: null,
                          institution: '',
                          year: null,
                          ongoing: false,
                        },
                      ];
                      setFieldValue('education', newEducation);
                    }}
                  >
                    + {t('intelCandidate.form.education.addEducation')}
                  </Button>
                </div>
                {values.education.length === 0 ? (
                  <div className={classes.emptyState}>
                    <span className={classes.emptyStateText}>
                      {t('intelCandidate.form.education.emptyState')}
                    </span>
                  </div>
                ) : (
                  values.education.map((edu, index) => (
                    <div
                      key={`education-${edu.id || index}`}
                      className={classes.repeaterCard}
                    >
                      <div className={`${classes.row} ${classes.cols3}`}>
                        <FormDropdown
                          name={`education.${index}.degree`}
                          label={t('intelCandidate.form.education.degree')}
                          placeholder="Select Degree/Level"
                          value={edu.degree}
                          setFieldValue={setFieldValue}
                          setFieldTouched={setFieldTouched}
                          error={errors.education?.[index]?.degree}
                          touched={touched.education?.[index]?.degree}
                          fetchOptions={{
                            dataName: 'educationOptionsBasicInfo',
                            getDataHandler: ({ pageNumber, term }) =>
                              AdminService.getEducations({
                                orderBy: 'sortOrder',
                                sortOrder: 'asc',
                                term,
                                pageNumber,
                              }),
                          }}
                          height={45}
                        />
                        <TextInput
                          name={`education.${index}.institution`}
                          label={t('intelCandidate.form.education.institution')}
                          placeholder="Enter Institution"
                          value={edu.institution}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={errors.education?.[index]?.institution}
                          touched={touched.education?.[index]?.institution}
                          setFieldTouched={setFieldTouched}
                          setFieldValue={setFieldValue}
                          prefilled={isPrefilledFromResume(
                            `education.${index}.institution`
                          )}
                          height={45}
                        />
                        <TextInputSwitch
                          name={`education.${index}.year`}
                          label={t(
                            'intelCandidate.form.education.completionYear'
                          )}
                          placeholder="YYYY"
                          value={edu.year}
                          onChange={(event) =>
                            formik?.setFieldValue(
                              `education.${index}.year`,
                              event.target.value.replace(/\D/g, '')
                            )
                          }
                          onBlur={formik.handleBlur}
                          height={45}
                          maxLength={4}
                          readonly={edu.ongoing}
                          error={errors.education?.[index]?.year}
                          touched={touched.education?.[index]?.year}
                          setFieldTouched={setFieldTouched}
                          setFieldValue={setFieldValue}
                          switchName={`education.${index}.ongoing`}
                          switchValue={edu.ongoing}
                          switchLabel={t(
                            'intelCandidate.form.education.ongoing'
                          )}
                          onSwitchChange={(v) => {
                            formik.setFieldValue(
                              `education.${index}.ongoing`,
                              v
                            );
                            if (v === true) {
                              formik.setFieldValue(
                                `education.${index}.year`,
                                ''
                              );
                              formik.setFieldTouched(
                                `education.${index}.year`,
                                true
                              );
                            }
                          }}
                        />
                      </div>
                      <div className={`${classes.row} ${classes.cols1}`}>
                        <div className={classes.deleteButtonWrapper}>
                          <button
                            type="button"
                            className={classes.deleteButton}
                            onClick={() => {
                              const newEducation = [...values.education];
                              newEducation.splice(index, 1);
                              setFieldValue('education', newEducation);
                              setFieldTouched('education', true, false);
                            }}
                          >
                            {t('common.delete')}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Skills Section */}
              <div id="section-Skills" className={classes.section}>
                <h2 className={classes.sectionTitle}>
                  {t('intelCandidate.form.tabs.Skills')}
                </h2>
                <div className={`${classes.row} ${classes.cols1}`}>
                  <ChipsDropdown
                    name="skills.key"
                    label={t('intelCandidate.form.skills.keySkills')}
                    maxLength={50}
                    height={150}
                    value={values.skills.key}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    touched={touched.skills?.key}
                    error={errors.skills?.key}
                    fetchOptions={{
                      dataName: 'skillsOptionsBasicInfo',
                      getDataHandler: ({ pageNumber, term }) =>
                        AdminService.getSkills({
                          orderBy: 'sortOrder',
                          sortOrder: 'asc',
                          term,
                          pageNumber,
                        }),
                    }}
                  />
                </div>
                {potentialSkills.length > 0 &&
                  (() => {
                    const selectedLabelSet = new Set(
                      (values.skills.key || [])
                        .map((v) => String(v?.label || '').toLowerCase())
                        .filter(Boolean)
                    );
                    const visibleSuggestions = potentialSkills.filter(
                      (name) => !selectedLabelSet.has(name.toLowerCase())
                    );
                    if (!visibleSuggestions.length) return null;
                    return (
                      <div
                        className={`${classes.row} ${classes.cols1}`}
                        data-testid="potential-skills-suggestions"
                      >
                        <div className={classes.potentialSkills}>
                          <div className={classes.potentialSkillsHeader}>
                            <span className={classes.potentialSkillsTitle}>
                              {t(
                                'intelCandidate.form.skills.potentialSkills.title',
                                'Suggested skills from resume'
                              )}
                            </span>
                            <span className={classes.potentialSkillsHint}>
                              {t(
                                'intelCandidate.form.skills.potentialSkills.hint',
                                'These skills were found in the resume but are not in the skill library. Click + to add to the library and this candidate.'
                              )}
                            </span>
                          </div>
                          <div className={classes.potentialSkillsList}>
                            {visibleSuggestions.map((name) => {
                              const isAdding = addingPotentialSkill === name;
                              return (
                                <div
                                  key={name}
                                  className={classes.potentialSkillChip}
                                >
                                  <span
                                    className={classes.potentialSkillName}
                                    title={name}
                                  >
                                    {name}
                                  </span>
                                  <button
                                    type="button"
                                    className={classes.potentialSkillAdd}
                                    disabled={!!addingPotentialSkill}
                                    onClick={() => adoptPotentialSkill(name)}
                                    aria-label={`Add ${name} to skill library`}
                                    title="Add to skill library and this candidate"
                                  >
                                    {isAdding ? '…' : '+'}
                                  </button>
                                  <button
                                    type="button"
                                    className={classes.potentialSkillDismiss}
                                    disabled={!!addingPotentialSkill}
                                    onClick={() => dismissPotentialSkill(name)}
                                    aria-label={`Dismiss ${name}`}
                                    title="Dismiss"
                                  >
                                    ×
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                <div className={`${classes.row} ${classes.cols1}`}>
                  <TextInput
                    name="skills.certifications"
                    label={t('intelCandidate.form.skills.certifications')}
                    placeholder="Enter certifications separated by comma"
                    value={values.skills.certifications}
                    note={t('common.optional')}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    height={45}
                  />
                </div>
                <div className={`${classes.row} ${classes.cols1}`}>
                  <div className={classes.radioGroup}>
                    <span className={classes.radioLabel}>
                      {t('intelCandidate.form.skills.upskill')}
                    </span>
                    <span className={classes.optional}>
                      ({t('common.optional')})
                    </span>
                    <div className={classes.radioOptions}>
                      <RadioButton
                        label="Yes"
                        isSelected={values.skills.upskill === true}
                        onClick={() => setFieldValue('skills.upskill', true)}
                      />
                      <RadioButton
                        label="No"
                        isSelected={values.skills.upskill === false}
                        onClick={() => setFieldValue('skills.upskill', false)}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Compensation & Availability Section */}
              <div id="section-Comp & Availability" className={classes.section}>
                <h2 className={classes.sectionTitle}>
                  {t('intelCandidate.form.tabs.Comp & Availability')}
                </h2>

                {/* Engagement Type */}
                <div className={`${classes.row} ${classes.cols3}`}>
                  <FormDropdown
                    name="compensation.engagement_type"
                    label={t('intelCandidate.form.compensation.engagementType')}
                    placeholder="Select Engagement Type"
                    value={values.compensation.engagement_type}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    fetchOptions={{
                      dataName: 'engagementTypeOptionsBasicInfo',
                      getDataHandler: ({ pageNumber, term }) =>
                        AdminService.getEngagementTypes({
                          orderBy: 'sortOrder',
                          sortOrder: 'asc',
                          term,
                          pageNumber,
                        }),
                    }}
                    autoSelectFirst
                    error={errors.compensation?.engagement_type}
                    touched={touched.compensation?.engagement_type}
                    height={45}
                  />
                </div>

                {/* Current Compensation */}
                <h3 className={classes.subsectionTitle}>
                  Current Compensation
                </h3>
                <div className={`${classes.row} ${classes.cols6}`}>
                  <TextInput
                    name="compensation.current.amount"
                    label={t('intelCandidate.form.compensation.currentAmount')}
                    placeholder="Enter Amount"
                    value={values.compensation.current.amount}
                    onChange={(event) =>
                      formik?.setFieldValue(
                        'compensation.current.amount',
                        event.target.value.replace(/\D/g, '')
                      )
                    }
                    onBlur={formik.handleBlur}
                    height={45}
                    note={t('common.optional')}
                  />
                  <FormDropdown
                    name="compensation.current.currency"
                    label={t(
                      'intelCandidate.form.compensation.currentCurrency'
                    )}
                    value={values.compensation.current.currency}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    options={currencyOptions}
                    height={45}
                  />
                  <FormDropdown
                    name="compensation.current.frequency"
                    label={t(
                      'intelCandidate.form.compensation.currentFrequency'
                    )}
                    value={values.compensation.current.frequency}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    options={frequencyOptions}
                    height={45}
                  />
                  <div className={classes.fxEquivalent}>
                    <span className={classes.label}>
                      {t('intelCandidate.form.compensation.fxEquivalent')}
                    </span>
                    <div className={classes.content}>
                      <div className={classes.value}>
                        {formatFx(currentFxValue, currentTargetCode)}
                      </div>
                    </div>
                  </div>
                  <FormDropdown
                    name="compensation.current.fx_target_currency"
                    label={t(
                      'intelCandidate.form.compensation.fxTargetCurrency'
                    )}
                    value={values.compensation.current.fx_target_currency}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    options={currencyOptions}
                    height={45}
                  />
                  <FormDropdown
                    name="compensation.current.fx_target_frequency"
                    label={t(
                      'intelCandidate.form.compensation.fxTargetFrequency'
                    )}
                    value={values.compensation.current.fx_target_frequency}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    options={frequencyOptions}
                    height={45}
                  />
                </div>
                <div className={`${classes.row} ${classes.cols1}`}>
                  <Textarea
                    name="compensation.current.note"
                    label={t('intelCandidate.form.compensation.currentNote')}
                    placeholder="Notes on bonus/variable pay, allowances, etc"
                    value={values.compensation.current.note}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    height={75}
                    prefilled={isPrefilledFromResume(
                      'compensation.current.note'
                    )}
                    maxLength={250}
                    note={t('common.optional')}
                  />
                </div>

                {/* Expected Compensation */}
                <div className={classes.subsectionTitleWithBadge}>
                  <h3 className={classes.subsectionTitle}>
                    Expected Compensation
                  </h3>
                  {(() => {
                    const cur = values?.compensation?.current;
                    const exp = values?.compensation?.expected;
                    const curAmt = Number(cur?.amount ?? 0);
                    const expAmt = Number(exp?.amount ?? 0);
                    const curCurr = cur?.currency?.value;
                    const expCurr = exp?.currency?.value;
                    const curFreq = cur?.frequency?.value;
                    const expFreq = exp?.frequency?.value;

                    // Prereq: both amounts, currency, and freq
                    if (
                      !curAmt ||
                      !expAmt ||
                      !curCurr ||
                      !expCurr ||
                      !curFreq ||
                      !expFreq
                    ) {
                      return null;
                    }

                    // Compute annual USD using same snapshot
                    const toAnnualUsd = (amount, currency, freq) => {
                      if (!fxSnapshot?.rates) return null;
                      const factor = getPeriodFactor(freq);
                      const baseAnnual = amount * factor;
                      const rate = fxSnapshot.rates[currency];
                      if (!rate) return null;
                      return baseAnnual / rate;
                    };

                    const curUsd = toAnnualUsd(curAmt, curCurr, curFreq);
                    const expUsd = toAnnualUsd(expAmt, expCurr, expFreq);
                    if (!curUsd || !expUsd || curUsd === 0) return null;

                    const rawPct = ((expUsd - curUsd) / curUsd) * 100;
                    // Round to nearest 5%
                    const rounded = Math.round(rawPct / 5) * 5;

                    let label = 'Same';
                    let tone = 'deltaNeutral';
                    if (rounded > 0) {
                      label = `Up ${Math.abs(rounded)}%`;
                      if (rounded <= 10) tone = 'deltaGreen';
                      else if (rounded <= 30) tone = 'deltaOrange';
                      else tone = 'deltaRed';
                    } else if (rounded < 0) {
                      label = `Down ${Math.abs(rounded)}%`;
                      // Down badges: use red for visibility
                      tone = 'deltaRed';
                    }

                    return (
                      <div
                        className={`${classes.deltaBadge} ${classes[tone]}`}
                        title={`${rawPct.toFixed(1)}% vs current`}
                      >
                        <span className={classes.deltaText}>{label}</span>
                      </div>
                    );
                  })()}
                </div>
                <div className={`${classes.row} ${classes.cols6}`}>
                  <TextInput
                    name="compensation.expected.amount"
                    label={t('intelCandidate.form.compensation.expectedAmount')}
                    placeholder="Enter Amount"
                    value={values.compensation.expected.amount}
                    onChange={(event) =>
                      formik?.setFieldValue(
                        'compensation.expected.amount',
                        event.target.value.replace(/\D/g, '')
                      )
                    }
                    onBlur={formik.handleBlur}
                    prefilled={isPrefilledFromResume(
                      'compensation.expected.amount'
                    )}
                    height={45}
                    note={t('common.optional')}
                  />
                  <FormDropdown
                    name="compensation.expected.currency"
                    label={t(
                      'intelCandidate.form.compensation.expectedCurrency'
                    )}
                    value={values.compensation.expected.currency}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    options={currencyOptions}
                    height={45}
                  />
                  <FormDropdown
                    name="compensation.expected.frequency"
                    label={t(
                      'intelCandidate.form.compensation.expectedFrequency'
                    )}
                    value={values.compensation.expected.frequency}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    options={frequencyOptions}
                    height={45}
                  />
                  <div className={classes.fxEquivalent}>
                    <span className={classes.label}>
                      {t('intelCandidate.form.compensation.fxEquivalent')}
                    </span>
                    <div className={classes.content}>
                      <div className={classes.value}>
                        {formatFx(expectedFxValue, expectedTargetCode)}
                      </div>
                    </div>
                  </div>
                  <FormDropdown
                    name="compensation.expected.fx_target_currency"
                    label={t(
                      'intelCandidate.form.compensation.fxTargetCurrency'
                    )}
                    value={values.compensation.expected.fx_target_currency}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    options={currencyOptions}
                    height={45}
                  />
                  <FormDropdown
                    name="compensation.expected.fx_target_frequency"
                    label={t(
                      'intelCandidate.form.compensation.fxTargetFrequency'
                    )}
                    value={values.compensation.expected.fx_target_frequency}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    options={frequencyOptions}
                    height={45}
                  />
                </div>
                <div className={`${classes.row} ${classes.cols1}`}>
                  <Textarea
                    name="compensation.expected.note"
                    label={t('intelCandidate.form.compensation.expectedNote')}
                    placeholder="Notes on bonus/variable pay, allowances, etc"
                    value={values.compensation.expected.note}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    maxLength={250}
                    note={t('common.optional')}
                    height={75}
                  />
                </div>

                {/* Availability */}
                <h3 className={classes.subsectionTitle}>
                  Availability
                  <div className={classes.toggleGroup}>
                    <Switch
                      label={t(
                        'intelCandidate.form.availability.immediateJoiner'
                      )}
                      isEnabled={values.availability.immediate}
                      onClick={() => {
                        const next = !values.availability.immediate;
                        setFieldValue('availability.immediate', next);
                        if (next === true) {
                          setFieldValue(
                            'availability.notice_period_days',
                            null
                          );
                        } else {
                          setFieldValue(
                            'availability.notice_period_days',
                            null
                          );
                        }
                      }}
                    />
                  </div>
                </h3>

                <div className={`${classes.flexRow}`}>
                  <FormDropdown
                    name="availability.notice_period_days"
                    label={t('intelCandidate.form.availability.noticePeriod')}
                    value={values.availability.notice_period_days}
                    setFieldValue={setFieldValue}
                    placeholder="Select Notice Period"
                    setFieldTouched={setFieldTouched}
                    fetchOptions={{
                      dataName: 'noticePeriodOptions',
                      getDataHandler: ({ pageNumber, term }) =>
                        AdminService.getNoticePeriods({
                          orderBy: 'sortOrder',
                          sortOrder: 'asc',
                          term,
                          pageNumber,
                        }),
                    }}
                    autoSelectFirst={values.availability.immediate === true}
                    readonly={values.availability.immediate === true}
                    height={45}
                  />
                  <DatePicker
                    name="availability.from_date"
                    label={t(
                      'intelCandidate.form.availability.availabilityFrom'
                    )}
                    value={values.availability.from_date}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    height={45}
                    newCalendar
                    note={t('common.optional')}
                  />
                  <FormDropdown
                    name="availability.shift"
                    label={t('intelCandidate.form.availability.workShift')}
                    value={values.availability.shift}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    placeholder="Select Shift"
                    fetchOptions={{
                      dataName: 'shiftOptions',
                      getDataHandler: ({ pageNumber, term }) =>
                        AdminService.getShifts({
                          orderBy: 'sortOrder',
                          sortOrder: 'asc',
                          term,
                          pageNumber,
                        }),
                    }}
                    height={45}
                    note={t('common.optional')}
                  />
                  <FormDropdown
                    name="availability.shift_readiness"
                    label={t('intelCandidate.form.availability.shiftReadiness')}
                    value={values.availability.shift_readiness}
                    setFieldValue={setFieldValue}
                    placeholder="Select Shift Readiness"
                    setFieldTouched={setFieldTouched}
                    fetchOptions={{
                      dataName: 'shiftReadinessOptions',
                      getDataHandler: ({ pageNumber, term }) =>
                        AdminService.getWorkShiftReadinesses({
                          orderBy: 'sortOrder',
                          sortOrder: 'asc',
                          term,
                          pageNumber,
                        }),
                    }}
                    height={45}
                    note={t('common.optional')}
                  />
                </div>

                <div className={`${classes.row} ${classes.cols3}`}>
                  <FormDropdown
                    name="availability.tz_overlap"
                    label={t(
                      'intelCandidate.form.availability.timezoneOverlap'
                    )}
                    value={values.availability.tz_overlap}
                    placeholder="Select Timezone Overlap"
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    options={timezoneOptions}
                    height={45}
                    note={t('common.optional')}
                  />
                  <FormDropdown
                    name="availability.work_mode"
                    label={t('intelCandidate.form.availability.workMode')}
                    value={values.availability.work_mode}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    placeholder="Select Work Mode"
                    fetchOptions={{
                      dataName: 'workModeOptions',
                      getDataHandler: ({ pageNumber, term }) =>
                        AdminService.getJobLocationTypes({
                          orderBy: 'sortOrder',
                          sortOrder: 'asc',
                          term,
                          pageNumber,
                        }),
                    }}
                    height={45}
                    note={t('common.optional')}
                  />
                  <FormDropdown
                    name="availability.preference"
                    label={t(
                      'intelCandidate.form.availability.engagementPreference'
                    )}
                    value={values.availability.preference}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    placeholder="Select Engagement Preference"
                    fetchOptions={{
                      dataName: 'engagementPreferenceOptions',
                      getDataHandler: ({ pageNumber, term }) =>
                        AdminService.getEngagementPreferences({
                          orderBy: 'sortOrder',
                          sortOrder: 'asc',
                          term,
                          pageNumber,
                        }),
                    }}
                    height={45}
                    note={t('common.optional')}
                  />
                </div>

                <div className={`${classes.flexRow}`}>
                  <div className={classes.travelGroup}>
                    <span className={classes.radioLabel}>
                      {t('intelCandidate.form.availability.willingToTravel')}
                      <span className={classes.optional}>
                        ({t('common.optional')})
                      </span>
                    </span>
                    <div className={classes.radioOptions}>
                      <RadioButton
                        label="Yes"
                        isSelected={values.availability.travel.willing === true}
                        onClick={() =>
                          setFieldValue('availability.travel.willing', true)
                        }
                      />
                      <RadioButton
                        label="No"
                        isSelected={
                          values.availability.travel.willing === false
                        }
                        onClick={() => {
                          setFieldValue('availability.travel.willing', false);
                          setFieldValue('availability.travel.percent', {
                            label: '0%',
                            value: '0',
                          });
                        }}
                      />
                    </div>
                  </div>
                  <FormDropdown
                    name="availability.travel.percent"
                    label={t(
                      'intelCandidate.form.availability.travelPercentage'
                    )}
                    value={values.availability.travel.percent}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    options={travelPercentOptions}
                    placeholder="0% - 100%"
                    height={45}
                    note={t('common.optional')}
                    readonly={values.availability.travel.willing !== true}
                  />
                  <div className={classes.relocateGroup}>
                    <span className={classes.radioLabel}>
                      {t('intelCandidate.form.availability.willingToRelocate')}
                      <span className={classes.optional}>
                        ({t('common.optional')})
                      </span>
                    </span>
                    <div className={classes.radioOptions}>
                      <RadioButton
                        label="Yes"
                        isSelected={
                          values.availability.relocate.willing === true
                        }
                        onClick={() =>
                          setFieldValue('availability.relocate.willing', true)
                        }
                      />
                      <RadioButton
                        label="No"
                        isSelected={
                          values.availability.relocate.willing === false
                        }
                        onClick={() => {
                          setFieldValue('availability.relocate.willing', false);
                          setFieldValue('availability.relocate.cities', null);
                        }}
                      />
                    </div>
                  </div>
                  <TextInput
                    name="availability.relocate.cities"
                    label={t('intelCandidate.form.availability.relocateCities')}
                    placeholder="Enter 3 Cities"
                    value={values.availability.relocate.cities}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    prefilled={isPrefilledFromResume(
                      'availability.relocate.cities'
                    )}
                    height={45}
                    note={t('common.optional')}
                    readonly={values.availability.relocate.willing !== true}
                  />
                </div>
              </div>

              {/* Documents Section */}
              <div id="section-Documents" className={classes.section}>
                <h2 className={classes.sectionTitle}>
                  {t('intelCandidate.form.tabs.Documents')}
                </h2>
                <div className={`${classes.row} ${classes.cols3}`}>
                  <TextInput
                    name="documents.linkedin"
                    label={t('intelCandidate.form.documents.linkedinUrl')}
                    placeholder="https://linkedin.com/in/username"
                    value={values.documents.linkedin}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={errors.documents?.linkedin}
                    touched={touched.documents?.linkedin}
                    prefilled={isPrefilledFromResume('documents.linkedin')}
                    height={45}
                  />
                  <TextInput
                    name="documents.github"
                    label={t('intelCandidate.form.documents.githubUrl')}
                    placeholder="https://github.com/username"
                    value={values.documents.github}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={errors.documents?.github}
                    touched={touched.documents?.github}
                    prefilled={isPrefilledFromResume('documents.github')}
                    height={45}
                    note={t('common.optional')}
                  />
                  <TextInput
                    name="documents.portfolio"
                    label={t('intelCandidate.form.documents.portfolioUrl')}
                    placeholder="https://portfolio.com"
                    value={values.documents.portfolio}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={errors.documents?.portfolio}
                    touched={touched.documents?.portfolio}
                    prefilled={isPrefilledFromResume('documents.portfolio')}
                    height={45}
                    note={t('common.optional')}
                  />
                </div>
                <div className={`${classes.row} ${classes.cols2}`}>
                  <SingleFileUploader
                    label={t('intelCandidate.form.documents.resume')}
                    name="documents.resume"
                    value={values.documents.resume}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    uploadImmediately
                    uploadingText={
                      t('intelCandidate.upload.uploading') || 'Uploading...'
                    }
                    uploadFailedText={
                      t('intelCandidate.errors.uploadFailed') || 'Upload failed'
                    }
                    placeholderTitle={
                      t('intelCandidate.form.documents.resumeTitle') || 'Resume'
                    }
                    placeholderHint={
                      t('intelCandidate.form.documents.resumeHint') ||
                      '(PDF, DOC, DOCX)'
                    }
                  />
                  <MultiFileUploader
                    label={`${t(
                      'intelCandidate.form.documents.additionalFiles'
                    )} (${t('common.optional')})`}
                    name="documents.additional"
                    values={values.documents.additional}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    uploadImmediately
                    uploadingText={
                      t('intelCandidate.upload.uploading') || 'Uploading...'
                    }
                    uploadFailedText={
                      t('intelCandidate.errors.uploadFailed') || 'Upload failed'
                    }
                    placeholderTitle={
                      t('intelCandidate.form.documents.otherFilesTitle') ||
                      'Other files'
                    }
                    placeholderHint={
                      t('intelCandidate.form.documents.otherFilesHint') ||
                      '(PDF, DOC, DOCX)'
                    }
                  />
                </div>
              </div>
            </div>

            <div className={classes.footerActions}>
              {(() => {
                const audits = candidate?.careerInfoAudits || [];
                const sortedAudits = [...audits].sort((a, b) => {
                  const dateA = moment(a?.createdAt);
                  const dateB = moment(b?.createdAt);
                  if (!dateA.isValid()) return 1;
                  if (!dateB.isValid()) return -1;
                  return dateB.valueOf() - dateA.valueOf(); // Descending order (latest first)
                });
                const latestAudit = sortedAudits[0];
                const createdAt = latestAudit?.createdAt;
                const isValidDate =
                  createdAt && !Number.isNaN(new Date(createdAt).getTime());

                return latestAudit && isValidDate ? (
                  <span className={classes.modifiedLabel}>
                    Modified by{' '}
                    <span className={classes.modifiedLabelBlack}>
                      {latestAudit?.user?.userProfile?.firstName}{' '}
                      {latestAudit?.user?.userProfile?.lastName}
                    </span>{' '}
                    on{' '}
                    <span className={classes.modifiedLabelBlack}>
                      {moment(createdAt).format('MMM DD, YYYY hh:mm A')}
                    </span>
                  </span>
                ) : null;
              })()}
              <div className={classes.footerButtons}>
                <Button
                  height={45}
                  onClick={handleSave}
                  disabled={!allRequiredFieldsCompleted}
                >
                  Save
                </Button>
                {!isEditMode && (
                  <Button
                    height={45}
                    style={{ display: 'none' }}
                    onClick={handleSubmit}
                    disabled={!allRequiredFieldsCompleted}
                  >
                    {t('intelCandidate.form.actions.submitCandidate')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        );
      }}
    </Formik>
  );
}
